import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { adminAPI } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement,
  Filler, Title, Tooltip as ChartTooltip, Legend as ChartLegend
} from 'chart.js';
import { Line as ChartLine } from 'react-chartjs-2';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

ChartJS.register(
  CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement,
  Filler, Title, ChartTooltip, ChartLegend
);
import {
  MARKET_DATA_COLORS, TRANSFORMS, PCT_TRANSFORMS,
  applyTransform, getTransformLabel, makeMarketDataChartOptions,
  getTransformsForFreq,
} from '../utils/marketDataTransforms';
import './BacktestDashboardApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

// Text input that buffers keystrokes locally and only commits on blur / Enter.
// Used for the Data Configuration metadata cells so a row doesn't re-categorise
// (and jump to another tab) on every keystroke — it moves once you click out.
function CommitInput({ value, onCommit, ...props }) {
  const [draft, setDraft] = useState(value ?? '');
  const editing = useRef(false);
  // Re-sync from the source of truth only while not actively editing this cell.
  useEffect(() => { if (!editing.current) setDraft(value ?? ''); }, [value]);
  return (
    <input
      {...props}
      value={draft}
      onFocus={() => { editing.current = true; }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { editing.current = false; if ((value ?? '') !== draft) onCommit(draft); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); else if (e.key === 'Escape') { setDraft(value ?? ''); editing.current = false; e.target.blur(); } }}
    />
  );
}

// Colour palette for chart overlays
const COLORS = [
  '#072040', // Primary
  '#f0a500', // Secondary gold
  '#91CF9B', // Success green
  '#599BE3', // Info blue
  '#dc3545', // Danger red
  '#6c757d', // Gray
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#ff9800', // Orange
  '#4caf50', // Green
];

// ============= Ticker Parsing (matches backend parse_tickers) =============
function parseTickers(tickersStr) {
  if (!tickersStr) return [];
  const result = [];
  let current = '';
  let parenDepth = 0;
  for (const char of tickersStr) {
    if (char === '(') { parenDepth++; current += char; }
    else if (char === ')') { parenDepth--; current += char; }
    else if (char === ',' && parenDepth === 0) {
      if (current.trim()) result.push(current.trim());
      current = '';
    } else { current += char; }
  }
  if (current.trim()) result.push(current.trim());
  return result;
}

// ============= FilterSidebar =============
function FilterSidebar({ filterOptions, filters, onFilterChange, onApply, onClear, getTickerLabel }) {
  if (!filterOptions) {
    return (
      <aside className="backtest-sidebar">
        <h2>Filters</h2>
        <div className="backtest-loading">Loading filters...</div>
      </aside>
    );
  }

  return (
    <aside className="backtest-sidebar">
      <h2>Filters</h2>

      <div className="backtest-filter-group">
        <label>Signal Type</label>
        <select value={filters.signal_type} onChange={(e) => onFilterChange('signal_type', e.target.value)}>
          <option value="">All Types</option>
          {filterOptions.signal_types.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="backtest-filter-group">
        <label>Signal Name</label>
        <input
          type="text"
          placeholder="Search by name..."
          value={filters.signal_name}
          onChange={(e) => onFilterChange('signal_name', e.target.value)}
        />
      </div>

      <div className="backtest-filter-group">
        <label>Long Asset</label>
        <select value={filters.long_asset} onChange={(e) => onFilterChange('long_asset', e.target.value)}>
          <option value="">All Assets</option>
          {filterOptions.long_assets.map(asset => (
            <option key={asset} value={asset}>{getTickerLabel ? getTickerLabel(asset) : asset}</option>
          ))}
        </select>
      </div>

      <div className="backtest-filter-group">
        <label>Short Asset</label>
        <select value={filters.short_asset} onChange={(e) => onFilterChange('short_asset', e.target.value)}>
          <option value="">All Assets</option>
          {filterOptions.short_assets.map(asset => (
            <option key={asset} value={asset}>{getTickerLabel ? getTickerLabel(asset) : asset}</option>
          ))}
        </select>
      </div>

      <div className="backtest-filter-group">
        <label>Transformation</label>
        <select value={filters.transformation_type} onChange={(e) => onFilterChange('transformation_type', e.target.value)}>
          <option value="">All Transformations</option>
          {filterOptions.transformation_types.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="backtest-filter-group">
        <label>Annualised Return Range (%)</label>
        <div className="backtest-range-inputs">
          <input type="number" placeholder="Min" step="0.01" value={filters.min_return} onChange={(e) => onFilterChange('min_return', e.target.value)} />
          <input type="number" placeholder="Max" step="0.01" value={filters.max_return} onChange={(e) => onFilterChange('max_return', e.target.value)} />
        </div>
      </div>

      <div className="backtest-filter-group">
        <label>Information Ratio Range</label>
        <div className="backtest-range-inputs">
          <input type="number" placeholder="Min" step="0.01" value={filters.min_ir} onChange={(e) => onFilterChange('min_ir', e.target.value)} />
          <input type="number" placeholder="Max" step="0.01" value={filters.max_ir} onChange={(e) => onFilterChange('max_ir', e.target.value)} />
        </div>
      </div>

      <div className="backtest-filter-group">
        <label>Run Date From</label>
        <input type="date" value={filters.date_from} onChange={(e) => onFilterChange('date_from', e.target.value)} />
      </div>

      <div className="backtest-filter-group">
        <label>Run Date To</label>
        <input type="date" value={filters.date_to} onChange={(e) => onFilterChange('date_to', e.target.value)} />
      </div>

      <div className="backtest-filter-group">
        <label>Favourite</label>
        <select value={filters.is_favourite} onChange={(e) => onFilterChange('is_favourite', e.target.value)}>
          <option value="">All</option>
          <option value="true">Favourites Only</option>
          <option value="false">Non-Favourites</option>
        </select>
      </div>

      <div className="backtest-filter-group">
        <label>Production</label>
        <select value={filters.is_production} onChange={(e) => onFilterChange('is_production', e.target.value)}>
          <option value="">All</option>
          <option value="true">Production Only</option>
          <option value="false">Non-Production</option>
        </select>
      </div>

      <button className="backtest-btn backtest-btn-primary" onClick={onApply}>Apply Filters</button>
      <button className="backtest-btn backtest-btn-secondary" onClick={onClear}>Clear All</button>
    </aside>
  );
}

// ============= SignalsTable =============
function SignalsTable({ signals, selectedIds, loading, onSelect, onSelectAll, onViewConfig, onEditConfig, onCopyConfig, getTickerLabel, onToggleFlag }) {
  const [sortKey, setSortKey] = useState('run_date');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedSignals = useMemo(() => {
    return [...signals].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [signals, sortKey, sortDir]);

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return (value * 100).toFixed(2) + '%';
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  const formatPvalue = (value) => {
    if (value === null || value === undefined) return '-';
    return value < 0.001 ? '<0.001' : value.toFixed(3);
  };

  const formatCI = (low, high) => {
    if (low === null || low === undefined || high === null || high === undefined) return '-';
    return `${low.toFixed(2)} to ${high.toFixed(2)}`;
  };

  const getValueClass = (value) => {
    if (value === null || value === undefined) return '';
    return value >= 0 ? 'backtest-positive' : 'backtest-negative';
  };

  // p-value colouring: significant (< 0.05) reads as a positive, else muted.
  const getPvalueClass = (value) => {
    if (value === null || value === undefined) return '';
    return value < 0.05 ? 'backtest-positive' : 'backtest-muted';
  };

  // A bootstrap IR CI that sits entirely above or below zero is significant.
  const ciExcludesZero = (low, high) => (
    low !== null && low !== undefined && high !== null && high !== undefined &&
    (low > 0 || high < 0)
  );

  const getSortClass = (key) => {
    if (sortKey !== key) return 'backtest-sortable';
    return sortDir === 'asc' ? 'backtest-sortable backtest-sorted-asc' : 'backtest-sortable backtest-sorted-desc';
  };

  if (loading) {
    return (
      <div className="backtest-table-container">
        <div className="backtest-loading">Loading signals...</div>
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div className="backtest-table-container">
        <div className="backtest-empty">No signals found. Try adjusting your filters.</div>
      </div>
    );
  }

  return (
    <div className="backtest-table-container">
      <div className="backtest-table-wrapper">
        <table className="backtest-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedIds.size === signals.length && signals.length > 0}
                  onChange={onSelectAll}
                />
              </th>
              <th style={{ width: '30px' }}></th>
              <th style={{ width: '30px' }}></th>
              <th className={getSortClass('signal_name')} onClick={() => handleSort('signal_name')}>Signal Name</th>
              <th className={getSortClass('signal_type')} onClick={() => handleSort('signal_type')}>Type</th>
              <th className={getSortClass('long_asset')} onClick={() => handleSort('long_asset')}>Long Asset</th>
              <th className={getSortClass('short_asset')} onClick={() => handleSort('short_asset')}>Short Asset</th>
              <th className={getSortClass('annualised_return')} onClick={() => handleSort('annualised_return')} title="Annualised strategy return: cumulative^(365/N) − 1">Ann. Return</th>
              <th className={getSortClass('information_ratio')} onClick={() => handleSort('information_ratio')} title="Annualised active return ÷ tracking error">Info Ratio</th>
              <th className={getSortClass('ir_tstat')} onClick={() => handleSort('ir_tstat')} title="Newey-West (autocorrelation-robust) t-statistic of the information ratio. |t| ≳ 2 is significant at the 5% level.">IR t-stat</th>
              <th className={getSortClass('ir_pvalue')} onClick={() => handleSort('ir_pvalue')} title="Two-sided p-value for IR ≠ 0 from the HAC t-statistic. Lower is stronger; < 0.05 = significant. Not corrected for multiple testing across the library.">IR p-val</th>
              <th title="95% confidence interval for the annualised IR from a moving-block bootstrap (robust to fat tails and skew). A CI that excludes 0 means the IR is significant without assuming normality.">IR 95% CI</th>
              <th className={getSortClass('true_alpha')} onClick={() => handleSort('true_alpha')} title="Geometric cumulative active return: ∏(1 + active_return) − 1">True Alpha</th>
              <th className={getSortClass('max_drawdown')} onClick={() => handleSort('max_drawdown')} title="Largest peak-to-trough decline in cumulative strategy return">Max DD</th>
              <th className={getSortClass('hit_rate')} onClick={() => handleSort('hit_rate')} title="Fraction of periods with positive strategy return">Hit Rate</th>
              <th className={getSortClass('skew')} onClick={() => handleSort('skew')} title="Skew of period strategy returns (positive = convex: many small losses, few big wins — the crisis-alpha shape)">Skew</th>
              <th className={getSortClass('tracking_error')} onClick={() => handleSort('tracking_error')} title="Annualised std of active returns (strategy − benchmark): √365 × σ">Track Error</th>
              <th className={getSortClass('annualised_turnover')} onClick={() => handleSort('annualised_turnover')} title="Annualised sum of absolute weight changes">Turnover</th>
              <th className={getSortClass('transformation_type')} onClick={() => handleSort('transformation_type')}>Transform</th>
              <th className={getSortClass('run_date')} onClick={() => handleSort('run_date')}>Run Date</th>
              <th>Config</th>
            </tr>
          </thead>
          <tbody>
            {sortedSignals.map(signal => (
              <tr key={signal.result_id} className={selectedIds.has(signal.result_id) ? 'backtest-selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(signal.result_id)}
                    onChange={() => onSelect(signal.result_id)}
                  />
                </td>
                <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '1rem' }}
                    onClick={() => onToggleFlag(signal.result_id, 'is_favourite', !signal.is_favourite)}
                    title={signal.is_favourite ? 'Remove favourite' : 'Mark as favourite'}>
                  {signal.is_favourite ? '\u2605' : '\u2606'}
                </td>
                <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                    onClick={() => onToggleFlag(signal.result_id, 'is_production', !signal.is_production)}
                    title={signal.is_production ? 'Remove from production' : 'Mark as production'}>
                  <span style={{
                    background: signal.is_production ? '#91CF9B' : '#E7E6E6',
                    color: signal.is_production ? '#072040' : '#6c757d',
                    padding: '2px 6px', borderRadius: '3px'
                  }}>P</span>
                </td>
                <td title={signal.signal_name}>{signal.signal_name || '-'}</td>
                <td>{signal.signal_type || '-'}</td>
                <td className="backtest-clip" title={signal.long_asset}>{getTickerLabel ? getTickerLabel(signal.long_asset) : (signal.long_asset || '-')}</td>
                <td className="backtest-clip" title={signal.short_asset}>{getTickerLabel ? getTickerLabel(signal.short_asset) : (signal.short_asset || '-')}</td>
                <td className={getValueClass(signal.annualised_return)}>{formatPercent(signal.annualised_return)}</td>
                <td className={getValueClass(signal.information_ratio)}>{formatNumber(signal.information_ratio)}</td>
                <td className={getValueClass(signal.ir_tstat)}>{formatNumber(signal.ir_tstat)}</td>
                <td className={getPvalueClass(signal.ir_pvalue)}>{formatPvalue(signal.ir_pvalue)}</td>
                <td className={ciExcludesZero(signal.ir_ci_low, signal.ir_ci_high) ? 'backtest-positive' : 'backtest-muted'}
                    title="95% bootstrap CI for the annualised IR">{formatCI(signal.ir_ci_low, signal.ir_ci_high)}</td>
                <td className={getValueClass(signal.true_alpha)}>{formatPercent(signal.true_alpha)}</td>
                <td className="backtest-negative">{formatPercent(signal.max_drawdown)}</td>
                <td>{formatPercent(signal.hit_rate)}</td>
                <td className={getValueClass(signal.skew)}>{formatNumber(signal.skew)}</td>
                <td>{formatPercent(signal.tracking_error)}</td>
                <td>{formatNumber(signal.annualised_turnover)}</td>
                <td>{signal.transformation_type || '-'}</td>
                <td>{formatDate(signal.run_date)}</td>
                <td>
                  <button className="backtest-btn-config" onClick={() => onViewConfig(signal.config_id, signal.signal_name)}>
                    View
                  </button>
                  <button className="backtest-btn-config backtest-btn-edit" onClick={() => onEditConfig(signal.config_id)} style={{ marginLeft: '4px' }}>
                    Edit
                  </button>
                  <button className="backtest-btn-config backtest-btn-copy" onClick={() => onCopyConfig(signal.config_id)} style={{ marginLeft: '4px' }}>
                    Copy
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="backtest-table-footer">
        Showing {signals.length} signals | {selectedIds.size} selected
      </div>
    </div>
  );
}

// ============= ChartsSection =============

// Helper: convert hex colour to rgba
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Helper: build a Chart.js dataset
function makeDataset(name, data, colorIndex, { fill = false, stepped = false } = {}) {
  const color = COLORS[colorIndex % COLORS.length];
  return {
    label: name,
    data,
    borderColor: color,
    backgroundColor: fill ? hexToRgba(color, 0.2) : color,
    fill,
    stepped,
    pointRadius: 0,
    borderWidth: 1.5,
    tension: 0,
    spanGaps: true,
  };
}

// Helper: build shared Chart.js options
function makeChartOptions(yFormat, yDomain) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      tooltip: {
        backgroundColor: '#072040',
        borderColor: '#f0a500',
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            return new Date(items[0].label).toLocaleDateString('en-AU');
          },
          label: (ctx) => {
            const val = ctx.parsed.y;
            if (val === null || val === undefined) return '';
            return `${ctx.dataset.label}: ${yFormat(val)}`;
          },
        },
      },
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            const d = new Date(label);
            return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
          },
          maxTicksLimit: 10,
          font: { size: 10 },
        },
        grid: { color: '#E7E6E6' },
      },
      y: {
        ticks: {
          callback: (v) => yFormat(v),
          font: { size: 10 },
        },
        grid: { color: '#E7E6E6' },
        ...(yDomain ? { min: yDomain[0], max: yDomain[1] } : {}),
      },
    },
    elements: { point: { radius: 0 }, line: { borderWidth: 1.5 } },
  };
}

// Convexity / crisis-alpha panel: the trend-following "smile" (mean monthly strategy
// return binned by market return) plus a skew / capture / crisis-payoff table.
// Recomputes on demand via /api/tactical/convexity for the compared signals.
function ConvexitySection({ streamData }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const signalNames = useMemo(() => {
    if (!streamData || !streamData.length || streamData[0].signal_name == null) return [];
    return [...new Set(streamData.map((d) => d.signal_name))];
  }, [streamData]);

  useEffect(() => {
    if (!signalNames.length) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    api.post('/api/tactical/convexity', { signal_names: signalNames })
      .then((res) => { if (!cancelled) setRows(res.data.results || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [signalNames.join('|')]);

  if (!signalNames.length) return null;
  const good = rows.filter((r) => !r.error && r.buckets && r.buckets.length);
  const num = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
  const pct = (v) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);

  const smileData = {
    datasets: good.map((r, i) => ({
      label: r.signal_name,
      data: r.buckets.map((b) => ({ x: b.x, y: b.y })),
      borderColor: COLORS[i % COLORS.length],
      backgroundColor: COLORS[i % COLORS.length],
      pointRadius: 3, borderWidth: 1.8, tension: 0.35, showLine: true, fill: false,
    })),
  };
  const smileOptions = {
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: {
        backgroundColor: '#072040', borderColor: '#f0a500', borderWidth: 1,
        titleColor: '#fff', bodyColor: '#fff',
        callbacks: { label: (c) => `${c.dataset.label}: market ${c.parsed.x.toFixed(1)}% → strategy ${c.parsed.y.toFixed(2)}%` },
      },
    },
    scales: {
      x: { type: 'linear', title: { display: true, text: 'Market (long asset) monthly return, %', font: { size: 11 } }, grid: { color: '#E7E6E6' }, ticks: { font: { size: 10 } } },
      y: { title: { display: true, text: 'Mean strategy monthly return, %', font: { size: 11 } }, grid: { color: '#E7E6E6' }, ticks: { font: { size: 10 } } },
    },
  };

  return (
    <div className="backtest-charts-section convexity-section">
      <h2>Convexity / Crisis Alpha</h2>
      <p className="convexity-note">
        Monthly returns. A genuine crisis hedge shows a “smile” — up at both tails — with positive
        skew, low down-capture, and a positive worst-decile (crisis) payoff.
      </p>
      <div className="convexity-chart">
        {loading ? <p className="backtest-muted">Computing…</p>
          : good.length ? <ChartLine data={smileData} options={smileOptions} />
            : <p className="backtest-muted">No convexity data for the selected signals.</p>}
      </div>
      <table className="convexity-table">
        <thead>
          <tr>
            <th>Signal</th>
            <th title="Skew of monthly strategy returns (positive = convex: many small losses, few big wins)">Skew</th>
            <th title="Mean strategy return ÷ mean market return in up months">Up-capture</th>
            <th title="Mean strategy return ÷ mean market return in down months (low/negative = defensive)">Down-capture</th>
            <th title="Annualised strategy return in the worst market-return decile of months">Crisis payoff (ann.)</th>
          </tr>
        </thead>
        <tbody>
          {good.map((r) => (
            <tr key={r.signal_name}>
              <td className="convexity-name">{r.signal_name}</td>
              <td className={r.skew > 0 ? 'convexity-good' : ''}>{num(r.skew)}</td>
              <td>{num(r.up_capture)}</td>
              <td className={r.down_capture !== null && r.down_capture < 0.5 ? 'convexity-good' : ''}>{num(r.down_capture)}</td>
              <td className={r.crisis_ann > 0 ? 'convexity-good' : (r.crisis_ann < -0.1 ? 'convexity-bad' : '')}>{pct(r.crisis_ann)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartsSection({ streamData, loading }) {
  const chartData = useMemo(() => {
    if (!streamData.length) return null;

    // Support both signal_name and combination_name as the name key
    const nameField = streamData[0].signal_name != null ? 'signal_name' : 'combination_name';
    const signalNames = [...new Set(streamData.map(d => d[nameField]))];

    // Group data by date
    const dateMap = new Map();
    streamData.forEach(d => {
      if (!dateMap.has(d.date)) dateMap.set(d.date, {});
      dateMap.get(d.date)[d[nameField]] = d;
    });

    const sortedDates = Array.from(dateMap.keys()).sort();
    const labels = sortedDates;

    // Metrics to extract per signal
    const metricKeys = {
      cumReturn: 'strategy_return_cumulative',
      alpha: 'true_alpha_cumulative',
      drawdown: 'strategy_drawdown',
      signal: 'signal',
      weight: 'portfolio_weight',
      ir: 'strategy_ir_rolling',
      hitRate: 'strategy_hit_rate_rolling',
      vol: 'strategy_vol_rolling',
    };

    // Build datasets for each chart metric
    const charts = {};
    Object.keys(metricKeys).forEach(key => { charts[key] = []; });

    signalNames.forEach((name, i) => {
      // Extract arrays for each metric
      const arrays = {};
      Object.keys(metricKeys).forEach(key => { arrays[key] = []; });

      sortedDates.forEach(date => {
        const row = dateMap.get(date)[name];
        Object.entries(metricKeys).forEach(([key, field]) => {
          arrays[key].push(row ? row[field] ?? null : null);
        });
      });

      // Create datasets
      Object.keys(metricKeys).forEach(key => {
        const opts = {};
        if (key === 'drawdown') opts.fill = true;
        if (key === 'weight') opts.stepped = 'after';
        charts[key].push(makeDataset(name, arrays[key], i, opts));
      });
    });

    return { labels, charts };
  }, [streamData]);

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return (value * 100).toFixed(1) + '%';
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(2);
  };

  if (loading) {
    return (
      <div className="backtest-charts">
        <div className="backtest-chart-card backtest-full-width">
          <div className="backtest-loading">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="backtest-charts">
        <div className="backtest-chart-card backtest-full-width">
          <div className="backtest-empty">Select signals to view charts</div>
        </div>
      </div>
    );
  }

  const { labels, charts } = chartData;

  return (
    <div className="backtest-charts">
      {/* Cumulative Returns Chart */}
      <div className="backtest-chart-card backtest-full-width">
        <h3 title="Geometric compounding of per-period strategy returns">Cumulative Strategy Returns</h3>
        <div style={{ height: 300 }}>
          <ChartLine data={{ labels, datasets: charts.cumReturn }} options={makeChartOptions(formatPercent)} />
        </div>
      </div>

      {/* True Alpha Chart */}
      <div className="backtest-chart-card">
        <h3 title="Geometric compounding of per-period active returns (strategy − benchmark)">Cumulative True Alpha</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.alpha }} options={makeChartOptions(formatPercent)} />
        </div>
      </div>

      {/* Drawdown Chart */}
      <div className="backtest-chart-card">
        <h3 title="Current cumulative return ÷ peak cumulative return − 1">Strategy Drawdown</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.drawdown }} options={makeChartOptions(formatPercent)} />
        </div>
      </div>

      {/* Signal Values Chart — only if data has signal values */}
      {charts.signal && charts.signal.some(ds => ds.data.some(v => v != null)) && (
        <div className="backtest-chart-card">
          <h3>Signal Values</h3>
          <div style={{ height: 250 }}>
            <ChartLine data={{ labels, datasets: charts.signal }} options={makeChartOptions(formatNumber)} />
          </div>
        </div>
      )}

      {/* Portfolio Weights Chart */}
      <div className="backtest-chart-card">
        <h3 title="Signal weight applied each period (after transformation and lookback shift)">Portfolio Weights</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.weight }} options={makeChartOptions(formatPercent, [-1, 1])} />
        </div>
      </div>

      {/* Rolling Information Ratio Chart */}
      <div className="backtest-chart-card">
        <h3 title="Annualised rolling active return ÷ rolling tracking error">Rolling Information Ratio</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.ir }} options={makeChartOptions(formatNumber)} />
        </div>
      </div>

      {/* Rolling Hit Rate Chart */}
      <div className="backtest-chart-card">
        <h3 title="Fraction of positive strategy returns over rolling window">Rolling Hit Rate</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.hitRate }} options={makeChartOptions(formatPercent, [0, 1])} />
        </div>
      </div>

      {/* Rolling Volatility Chart */}
      <div className="backtest-chart-card">
        <h3 title="√365 × rolling std of active returns">Rolling Volatility (Tracking Error)</h3>
        <div style={{ height: 250 }}>
          <ChartLine data={{ labels, datasets: charts.vol }} options={makeChartOptions(formatPercent)} />
        </div>
      </div>
    </div>
  );
}

// ============= ConfigModal =============

// Renders a key-value table for a flat object
function ConfigParamsTable({ data, labels }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
  if (!entries.length) return null;
  return (
    <table className="backtest-params-table">
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key}>
            <td className="backtest-params-label">{labels?.[key] || key.replace(/_/g, ' ')}</td>
            <td className="backtest-params-value">{String(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Renders the signal_config section
function SignalConfigSection({ signalConfig }) {
  if (!signalConfig) return null;

  const customExprs = signalConfig.custom_expressions || signalConfig.custom?.custom_expressions;
  const expression = signalConfig.expression || signalConfig.custom?.expression;

  return (
    <div className="backtest-config-section">
      <h4>Signal Construction</h4>
      {customExprs && Object.keys(customExprs).length > 0 && (
        <div className="backtest-expr-pipeline">
          <div className="backtest-pipeline-label">Intermediate Variables</div>
          <table className="backtest-params-table">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>Variable</th>
                <th>Expression</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(customExprs).map(([name, expr]) => (
                <tr key={name}>
                  <td className="backtest-params-label"><code>{name}</code></td>
                  <td><code className="backtest-code-inline">{expr}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {expression && (
        <div className="backtest-expr-final">
          <div className="backtest-pipeline-label">Final Signal Expression</div>
          <code className="backtest-code-block">{expression}</code>
        </div>
      )}
    </div>
  );
}

// Renders the transformation_config section
function TransformationSection({ transformConfig }) {
  if (!transformConfig) return null;

  const type = transformConfig.transformation_type;

  // Select type: conditions → choices mapping
  if (type === 'select' && transformConfig.select) {
    const { conditions, choices, default: defaultVal } = transformConfig.select;
    return (
      <div className="backtest-config-section">
        <h4>Transformation: <span className="backtest-transform-type">{type}</span></h4>
        <table className="backtest-params-table">
          <thead>
            <tr>
              <th>Condition</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Weight</th>
            </tr>
          </thead>
          <tbody>
            {conditions?.map((cond, i) => (
              <tr key={i}>
                <td><code className="backtest-code-inline">{cond}</code></td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>{choices?.[i]}</td>
              </tr>
            ))}
            <tr className="backtest-default-row">
              <td><em>Default (no condition met)</em></td>
              <td style={{ textAlign: 'center', fontWeight: 600 }}>{defaultVal ?? 0}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Custom type: single expression
  if (type === 'custom' && transformConfig.custom) {
    return (
      <div className="backtest-config-section">
        <h4>Transformation: <span className="backtest-transform-type">{type}</span></h4>
        <div className="backtest-expr-final">
          <div className="backtest-pipeline-label">Transformation Expression</div>
          <code className="backtest-code-block">{transformConfig.custom.expression}</code>
        </div>
      </div>
    );
  }

  // Logit types or other: show type + any extra params
  const extraParams = {};
  Object.entries(transformConfig).forEach(([k, v]) => {
    if (k !== 'transformation_type' && typeof v !== 'object') {
      extraParams[k] = v;
    }
  });

  return (
    <div className="backtest-config-section">
      <h4>Transformation: <span className="backtest-transform-type">{type}</span></h4>
      {Object.keys(extraParams).length > 0 && (
        <ConfigParamsTable data={extraParams} />
      )}
    </div>
  );
}

// Renders the data_source_config section
function DataSourceSection({ dataSourceConfig }) {
  if (!dataSourceConfig) return null;

  const { sql: sqlConfig, ...flatParams } = dataSourceConfig;

  return (
    <div className="backtest-config-section">
      <h4>Data Source</h4>
      <ConfigParamsTable
        data={flatParams}
        labels={{
          source_type: 'Source Type',
          use_cached_data: 'Use Cached Data',
          tickers: 'Tickers',
          long_asset: 'Long Asset',
          short_asset: 'Short Asset'
        }}
      />
      {sqlConfig && (
        <div className="backtest-expr-final" style={{ marginTop: '8px' }}>
          <div className="backtest-pipeline-label">SQL Query</div>
          <code className="backtest-code-block">{sqlConfig.query}</code>
        </div>
      )}
    </div>
  );
}

function ConfigModal({ configId, signalName, onClose, onRerun, onSaveAs, getTickerLabel }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    if (!configId) return;

    setLoading(true);
    setError(null);

    api.get(`/api/tactical/config/${configId}`)
      .then(res => {
        if (res.data.error) {
          setError(res.data.error);
        } else {
          setConfig(res.data);
        }
      })
      .catch(err => {
        console.error('Error loading config:', err);
        setError('Failed to load configuration');
      })
      .finally(() => setLoading(false));
  }, [configId]);

  if (!configId) return null;

  // Parse the config JSON — handle literal \n from sync escaping
  let parsed = null;
  if (config?.config_json) {
    let jsonStr = config.config_json;
    // The sync script escapes newlines to literal \n text in PostgreSQL.
    // Restore actual newlines before parsing, and handle other escaped chars.
    if (typeof jsonStr === 'string') {
      jsonStr = jsonStr
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\');
    }
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      // Try parsing the original string in case it was already clean
      try {
        parsed = JSON.parse(config.config_json);
      } catch {
        parsed = null;
      }
    }
  }

  const handleSaveRerun = async () => {
    if (!parsed || !config) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const formData = parseConfigForForm(config, parsed);
      const payload = { ...formData };
      if (!payload.custom_expressions || Object.keys(payload.custom_expressions).length === 0) payload.custom_expressions = null;
      if (!payload.analysis_rolling_window) payload.analysis_rolling_window = null;
      const res = await api.post('/api/tactical/backtest', payload);
      if (res.data.success) {
        setSaveMsg('Backtest re-run and saved successfully');
        if (onRerun) onRerun();
      } else {
        setSaveMsg('Error: ' + (res.data.detail || 'Backtest failed'));
      }
    } catch (err) {
      setSaveMsg('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsRerun = async () => {
    if (!parsed || !config || !saveAsName.trim()) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const formData = parseConfigForForm(config, parsed);
      formData.signal_name = saveAsName.trim();
      const payload = { ...formData };
      if (!payload.custom_expressions || Object.keys(payload.custom_expressions).length === 0) payload.custom_expressions = null;
      if (!payload.analysis_rolling_window) payload.analysis_rolling_window = null;
      const res = await api.post('/api/tactical/backtest', payload);
      if (res.data.success) {
        setSaveMsg(`Saved as "${saveAsName.trim()}" successfully`);
        setShowSaveAs(false);
        setSaveAsName('');
        if (onSaveAs) onSaveAs();
      } else {
        setSaveMsg('Error: ' + (res.data.detail || 'Backtest failed'));
      }
    } catch (err) {
      setSaveMsg('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Extract top-level analysis parameters
  const analysisParams = parsed ? {
    frequency: parsed.frequency,
    analysis_start: parsed.analysis_start,
    analysis_end: parsed.analysis_end,
    trade_cost: parsed.trade_cost,
    lookback_window: parsed.lookback_window,
    trade_period: parsed.trade_period,
    ...(parsed.analysis_rolling_window ? { analysis_rolling_window: parsed.analysis_rolling_window } : {})
  } : null;

  return (
    <div className="backtest-modal-overlay">
      <div className="backtest-modal-content" onClick={e => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>Configuration: {signalName || configId}</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="backtest-modal-body">
          {loading && <div className="backtest-loading">Loading configuration...</div>}

          {error && <div className="backtest-error">{error}</div>}

          {!loading && !error && config && (
            <>
              {/* Header metadata */}
              <div className="backtest-config-meta">
                <div className="backtest-config-meta-item">
                  <strong>Config ID:</strong> {config.config_id}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Signal Type:</strong> {config.signal_type || '-'}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Transformation:</strong> {config.transformation_type || '-'}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Created:</strong> {config.created_date ? new Date(config.created_date).toLocaleDateString('en-AU') : '-'}
                </div>
                {config.tickers && (
                  <div className="backtest-config-meta-item" style={{ gridColumn: 'span 2' }}>
                    <strong>Tickers:</strong> {getTickerLabel ? parseTickers(config.tickers).map(t => getTickerLabel(t)).join(', ') : config.tickers}
                  </div>
                )}
              </div>

              {parsed ? (
                <div className="backtest-config-structured">
                  {/* Analysis Parameters */}
                  {analysisParams && (
                    <div className="backtest-config-section">
                      <h4>Analysis Parameters</h4>
                      <ConfigParamsTable
                        data={analysisParams}
                        labels={{
                          frequency: 'Frequency',
                          analysis_start: 'Start Date',
                          analysis_end: 'End Date',
                          trade_cost: 'Trade Cost',
                          lookback_window: 'Lookback Window',
                          trade_period: 'Trade Period',
                          analysis_rolling_window: 'Rolling Window'
                        }}
                      />
                    </div>
                  )}

                  {/* Data Source */}
                  <DataSourceSection dataSourceConfig={parsed.data_source_config} />

                  {/* Signal Construction */}
                  <SignalConfigSection signalConfig={parsed.signal_config} />

                  {/* Transformation Rules */}
                  <TransformationSection transformConfig={parsed.transformation_config} />

                  {/* Save / Save As actions */}
                  <div className="config-modal-actions" style={{ marginTop: '16px', borderTop: '1px solid #E7E6E6', paddingTop: '12px' }}>
                    {saveMsg && (
                      <div className={saveMsg.startsWith('Error') ? 'backtest-error' : 'backtest-success'} style={{ marginBottom: '8px', fontSize: '0.85rem' }}>
                        {saveMsg}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        className="backtest-btn backtest-btn-primary"
                        onClick={handleSaveRerun}
                        disabled={saving}
                        style={{ padding: '8px 20px', fontSize: '0.85rem' }}
                      >
                        {saving ? 'Running...' : 'Re-run'}
                      </button>
                      <button
                        className="backtest-btn backtest-btn-secondary"
                        onClick={() => setShowSaveAs(!showSaveAs)}
                        disabled={saving}
                        style={{ padding: '8px 20px', fontSize: '0.85rem' }}
                      >
                        Save As
                      </button>
                    </div>
                    {showSaveAs && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                        <input
                          type="text"
                          placeholder="New signal name"
                          value={saveAsName}
                          onChange={(e) => setSaveAsName(e.target.value)}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid #599BE3', borderRadius: '4px', fontSize: '0.85rem' }}
                        />
                        <button
                          className="backtest-btn backtest-btn-primary"
                          onClick={handleSaveAsRerun}
                          disabled={saving || !saveAsName.trim()}
                          style={{ padding: '6px 16px', fontSize: '0.85rem' }}
                        >
                          {saving ? 'Running...' : 'Run & Save'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Raw JSON toggle */}
                  <div style={{ marginTop: '16px', borderTop: '1px solid #E7E6E6', paddingTop: '12px' }}>
                    <button
                      className="backtest-btn-config"
                      onClick={() => setShowRawJson(!showRawJson)}
                      style={{ marginBottom: showRawJson ? '8px' : 0 }}
                    >
                      {showRawJson ? 'Hide' : 'Show'} Raw JSON
                    </button>
                    {showRawJson && (
                      <pre className="backtest-config-json">{JSON.stringify(parsed, null, 2)}</pre>
                    )}
                  </div>
                </div>
              ) : (
                // Fallback: raw JSON if parsing failed — still unescape for readability
                <div className="backtest-config-json-container">
                  <h3>Configuration JSON</h3>
                  <pre className="backtest-config-json">{
                    (config.config_json || 'No configuration data')
                      .replace(/\\n/g, '\n')
                      .replace(/\\r/g, '\r')
                      .replace(/\\t/g, '\t')
                      .replace(/\\\\/g, '\\')
                  }</pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============= Signal Builder Components =============

const SIGNAL_TYPES = ['momentum', 'growth', 'sentiment', 'valuation', 'volatility', 'macro', 'technical', 'other'];

const TRANSFORMATION_TYPES = ['select', 'logit', 'logit_even', 'linear', 'percentile', 'quantile', 'custom'];

function TickerSelector({ availableTickers, selectedTickers, onChange, freq, getTickerLabel, longAsset, shortAsset, onLongChange, onShortChange }) {
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const tickers = freq === 'D' ? (availableTickers?.daily_tickers || []) : (availableTickers?.monthly_tickers || []);
  const metadata = availableTickers?.ticker_metadata || {};
  const selectedList = parseTickers(selectedTickers);

  const label = (t) => getTickerLabel ? getTickerLabel(t) : t;
  const isIndex = (t) => metadata[t]?.is_index === true;

  // Filter: match against code, label, region, type, style
  const filtered = search
    ? tickers.filter(t => {
        const q = search.toLowerCase();
        return t.toLowerCase().includes(q) ||
          label(t).toLowerCase().includes(q) ||
          (metadata[t]?.region || '').toLowerCase().includes(q) ||
          (metadata[t]?.type || '').toLowerCase().includes(q) ||
          (metadata[t]?.style || '').toLowerCase().includes(q);
      })
    : tickers;

  // Group filtered tickers by asset class
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(t => {
      const ac = metadata[t]?.asset_class || 'Unclassified';
      if (!groups[ac]) groups[ac] = [];
      groups[ac].push(t);
    });
    // Sort: named groups first alphabetically, then Unclassified last
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unclassified') return 1;
      if (b === 'Unclassified') return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map(key => ({ name: key, tickers: groups[key] }));
  }, [filtered, metadata]);

  const toggleTicker = (ticker) => {
    const newList = selectedList.includes(ticker)
      ? selectedList.filter(t => t !== ticker)
      : [...selectedList, ticker];
    onChange(newList.join(','));
  };

  const removeTicker = (ticker) => {
    const newList = selectedList.filter(t => t !== ticker);
    onChange(newList.join(','));
    // Clear long/short if removed
    if (ticker === longAsset) onLongChange('');
    if (ticker === shortAsset) onShortChange('');
  };

  // If a non-index ticker is set as long/short, clear it
  useEffect(() => {
    if (longAsset && !isIndex(longAsset) && metadata[longAsset]) onLongChange('');
    if (shortAsset && !isIndex(shortAsset) && metadata[shortAsset]) onShortChange('');
  }, [metadata, longAsset, shortAsset]);

  // Count total items for "more" indicator
  const totalFiltered = filtered.length;

  return (
    <div className="signal-builder-ticker-selector">
      {/* Selected chips */}
      {selectedList.length > 0 && (
        <div className="ticker-chips">
          {selectedList.map(t => {
            const tickerIsIndex = isIndex(t);
            return (
              <div key={t} className={`ticker-chip${t === longAsset ? ' long' : ''}${t === shortAsset ? ' short' : ''}${!tickerIsIndex && metadata[t] ? ' non-index' : ''}`}>
                <span className="ticker-chip-label" title={t}>{label(t)}</span>
                {tickerIsIndex && (
                  <div className="ticker-chip-roles">
                    <button type="button"
                      className={`ticker-chip-role${t === longAsset ? ' active' : ''}`}
                      onClick={() => onLongChange(t === longAsset ? '' : t)}
                      title="Set as long asset">L</button>
                    <button type="button"
                      className={`ticker-chip-role${t === shortAsset ? ' active' : ''}`}
                      onClick={() => onShortChange(t === shortAsset ? '' : t)}
                      title="Set as short asset">S</button>
                  </div>
                )}
                <button type="button" className="ticker-chip-remove" onClick={() => removeTicker(t)} title="Remove ticker">×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + toggle */}
      <div className="ticker-search-row">
        <input
          type="text"
          placeholder="Search tickers..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); if (!listOpen) setListOpen(true); }}
          onFocus={() => setListOpen(true)}
          className="signal-builder-input"
        />
        <button type="button" className="ticker-search-toggle" onClick={() => setListOpen(!listOpen)}>
          {listOpen ? '▲' : '▼'}
        </button>
      </div>

      {/* Dropdown list grouped by asset class */}
      {listOpen && (
        <div className="signal-builder-ticker-list">
          {grouped.map(group => (
            <div key={group.name}>
              <div className="ticker-group-header">{group.name}</div>
              {group.tickers.slice(0, 200).map(ticker => (
                <label key={ticker} className={`signal-builder-ticker-item${selectedList.includes(ticker) ? ' selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedList.includes(ticker)}
                    onChange={() => toggleTicker(ticker)}
                  />
                  <span className="ticker-item-label" title={ticker}>{label(ticker)}</span>
                  <span className="ticker-item-code">{ticker}</span>
                </label>
              ))}
            </div>
          ))}
          {totalFiltered > 200 && (
            <div className="signal-builder-ticker-more">...and {totalFiltered - 200} more (refine search)</div>
          )}
          {totalFiltered === 0 && <div className="signal-builder-ticker-more">No tickers found</div>}
        </div>
      )}
    </div>
  );
}

function TransformationConfig({ type, params, onChange }) {
  const updateParam = (key, value) => {
    onChange({ ...params, [key]: value });
  };

  if (type === 'logit' || type === 'logit_even' || type === 'linear' || type === 'percentile' || type === 'quantile') {
    const showK = type === 'logit' || type === 'logit_even';
    const showBuckets = type === 'quantile';
    const windowMode = (params?.standardisation_window && params.standardisation_window > 0) ? 'rolling' : 'expanding';

    return (
      <div className="signal-builder-transform-params">
        <div className="signal-builder-field-row">
          <div className="signal-builder-field">
            <label>Lower Bound</label>
            <input type="number" step="0.1" value={params?.lower_bound ?? -1}
              onChange={(e) => updateParam('lower_bound', parseFloat(e.target.value))} />
          </div>
          <div className="signal-builder-field">
            <label>Upper Bound</label>
            <input type="number" step="0.1" value={params?.upper_bound ?? 1}
              onChange={(e) => updateParam('upper_bound', parseFloat(e.target.value))} />
          </div>
          {showK && (
            <div className="signal-builder-field">
              <label>K Parameter</label>
              <input type="number" step="0.1" value={params?.k_parameter ?? 1}
                onChange={(e) => updateParam('k_parameter', parseFloat(e.target.value))} />
            </div>
          )}
          {showBuckets && (
            <div className="signal-builder-field">
              <label>Buckets</label>
              <input type="number" step="1" min="2" value={params?.buckets ?? 5}
                onChange={(e) => updateParam('buckets', parseInt(e.target.value))} />
            </div>
          )}
        </div>
        <div className="signal-builder-field-row" style={{ marginTop: '8px' }}>
          <div className="signal-builder-field">
            <label>Standardisation</label>
            <select value={windowMode}
              onChange={(e) => {
                if (e.target.value === 'expanding') updateParam('standardisation_window', 0);
                else updateParam('standardisation_window', 252);
              }}>
              <option value="expanding">Expanding</option>
              <option value="rolling">Rolling</option>
            </select>
          </div>
          {windowMode === 'rolling' && (
            <div className="signal-builder-field">
              <label>Window</label>
              <input type="number" step="1" min="10" value={params?.standardisation_window ?? 252}
                onChange={(e) => updateParam('standardisation_window', parseInt(e.target.value))} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === 'select') {
    const conditions = params?.select?.conditions || ["signal > 0"];
    const choices = params?.select?.choices || [1];
    const defaultVal = params?.select?.default ?? 0;

    const updateSelect = (newConditions, newChoices) => {
      onChange({ ...params, select: { conditions: newConditions, choices: newChoices, default: defaultVal } });
    };

    return (
      <div className="signal-builder-transform-params">
        <label>Select Conditions</label>
        {conditions.map((cond, i) => (
          <div key={i} className="signal-builder-field-row">
            <input type="text" placeholder="Condition (e.g. signal > 0)" value={cond}
              onChange={(e) => {
                const newC = [...conditions]; newC[i] = e.target.value;
                updateSelect(newC, choices);
              }}
              style={{ flex: 2 }} />
            <input type="number" step="0.1" placeholder="Choice" value={choices[i] ?? 0}
              onChange={(e) => {
                const newCh = [...choices]; newCh[i] = parseFloat(e.target.value);
                updateSelect(conditions, newCh);
              }}
              style={{ width: '80px' }} />
            <button className="signal-builder-btn-remove" onClick={() => {
              updateSelect(conditions.filter((_, j) => j !== i), choices.filter((_, j) => j !== i));
            }}>×</button>
          </div>
        ))}
        <button className="backtest-btn-config" onClick={() => updateSelect([...conditions, ''], [...choices, 0])}>+ Add Condition</button>
        <div className="signal-builder-field" style={{ marginTop: '8px' }}>
          <label>Default Value</label>
          <input type="number" step="0.1" value={defaultVal}
            onChange={(e) => onChange({ ...params, select: { conditions, choices, default: parseFloat(e.target.value) } })} />
        </div>
      </div>
    );
  }

  if (type === 'custom') {
    return (
      <div className="signal-builder-transform-params">
        <div className="signal-builder-field">
          <label>Custom Expression</label>
          <textarea rows={3} value={params?.custom?.expression || ''}
            onChange={(e) => onChange({ ...params, custom: { expression: e.target.value } })}
            placeholder="e.g. logit_curve(signal, -0.5, 0.5, 1.5)" />
        </div>
      </div>
    );
  }

  return null;
}

// ============= Transform Preview =============

function generateTransformPreview(type, params) {
  // Generate 200 synthetic signal values from -3 to +3 (z-score range)
  const n = 200;
  const points = [];
  const lb = params?.lower_bound ?? -1;
  const ub = params?.upper_bound ?? 1;
  const k = params?.k_parameter ?? 1;
  const buckets = params?.buckets ?? 5;

  for (let i = 0; i < n; i++) {
    const signal = -3 + (6 * i) / (n - 1);
    let weight = 0;

    if (type === 'logit') {
      const std = signal * 3;
      weight = (2 * (1 / (1 + Math.exp(k * (-std)))) - 1) * ub;
      weight = Math.max(lb, Math.min(ub, weight));
    } else if (type === 'logit_even') {
      const std = signal * 3;
      if (signal > 0) {
        weight = (2 * (1 / (1 + Math.exp(k * (-std)))) - 1) * ub;
      } else {
        weight = (2 * (1 / (1 + Math.exp(k * (-std)))) - 1) * lb * -1;
      }
      weight = Math.max(lb, Math.min(ub, weight));
    } else if (type === 'linear') {
      const slope = (ub - lb) / 3;
      weight = slope * signal;
      weight = Math.max(lb, Math.min(ub, weight));
    } else if (type === 'percentile') {
      // Approximate: for uniform z-scores, percentile is roughly the position
      const pct = i / (n - 1);
      weight = lb + (ub - lb) * pct;
    } else if (type === 'quantile') {
      const pct = i / (n - 1);
      const bucket = Math.min(Math.floor(pct * buckets), buckets - 1);
      weight = lb + (ub - lb) * bucket / (buckets - 1);
    } else if (type === 'select') {
      // Show select as step function using defined conditions
      const conditions = params?.select?.conditions || [];
      const choices = params?.select?.choices || [];
      const defaultVal = params?.select?.default ?? 0;
      weight = defaultVal;
      // Evaluate simple conditions like "signal > 0", "signal < -1" etc
      for (let ci = 0; ci < conditions.length; ci++) {
        const cond = conditions[ci];
        try {
          const result = evaluateSimpleCondition(cond, signal);
          if (result) { weight = choices[ci] ?? 0; break; }
        } catch { /* skip unparseable conditions */ }
      }
    } else {
      continue;
    }

    points.push({
      signal: parseFloat(signal.toFixed(3)),
      weight: parseFloat(weight.toFixed(4))
    });
  }

  return points;
}

function evaluateSimpleCondition(condStr, signalVal) {
  // Parse simple conditions: "signal > 0", "signal <= -1.5", "signal >= 0.5"
  const s = condStr.replace(/\s+/g, '');
  const ops = ['>=', '<=', '!=', '>', '<', '=='];
  for (const op of ops) {
    const idx = s.indexOf(op, s.indexOf('signal') + 6);
    if (idx === -1) continue;
    const rhs = parseFloat(s.slice(idx + op.length));
    if (isNaN(rhs)) continue;
    switch (op) {
      case '>': return signalVal > rhs;
      case '<': return signalVal < rhs;
      case '>=': return signalVal >= rhs;
      case '<=': return signalVal <= rhs;
      case '==': return Math.abs(signalVal - rhs) < 1e-9;
      case '!=': return Math.abs(signalVal - rhs) >= 1e-9;
      default: return false;
    }
  }
  return false;
}

function TransformPreview({ type, params }) {
  const data = useMemo(() => generateTransformPreview(type, params), [type, params]);

  if (!data.length) return null;

  // Determine y-axis domain from bounds
  const lb = params?.lower_bound ?? -1;
  const ub = params?.upper_bound ?? 1;
  const yMin = Math.min(lb, ...(data.map(d => d.weight)));
  const yMax = Math.max(ub, ...(data.map(d => d.weight)));
  const yPad = (yMax - yMin) * 0.1 || 0.1;

  return (
    <div className="transform-preview">
      <h4>Transform Response Curve</h4>
      <p className="transform-preview-subtitle">How standardised signal values map to portfolio weight</p>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E7E6E6" />
          <XAxis
            dataKey="signal"
            type="number"
            domain={[-3, 3]}
            tickCount={7}
            label={{ value: 'Signal (z-score)', position: 'insideBottom', offset: -2, style: { fontSize: 11, fill: '#6c757d' } }}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            domain={[yMin - yPad, yMax + yPad]}
            label={{ value: 'Weight', angle: -90, position: 'insideLeft', offset: 4, style: { fontSize: 11, fill: '#6c757d' } }}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            formatter={(val) => [val.toFixed(4), 'Weight']}
            labelFormatter={(val) => `Signal: ${val}`}
            contentStyle={{ fontSize: 12, borderColor: '#E7E6E6' }}
          />
          <ReferenceLine x={0} stroke="#6c757d" strokeDasharray="3 3" />
          <ReferenceLine y={0} stroke="#6c757d" strokeDasharray="3 3" />
          <Line
            type="monotone"
            dataKey="weight"
            stroke="#072040"
            strokeWidth={2}
            dot={false}
            name="Weight"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExpressionHelp({ show }) {
  if (!show) return null;
  return (
    <div className="signal-builder-help">
      <h4>Available Variables & Functions</h4>
      <table className="backtest-params-table">
        <tbody>
          <tr><td className="backtest-params-label"><code>df['long_asset']</code></td><td>Long asset price series</td></tr>
          <tr><td className="backtest-params-label"><code>df['short_asset']</code></td><td>Short asset price series</td></tr>
          <tr><td className="backtest-params-label"><code>df['TICKER(RI)']</code></td><td>Any ticker column by name</td></tr>
          <tr><td className="backtest-params-label"><code>.pct_change(n)</code></td><td>n-period percentage change</td></tr>
          <tr><td className="backtest-params-label"><code>.rolling(n).mean()</code></td><td>n-period rolling average</td></tr>
          <tr><td className="backtest-params-label"><code>.rolling(n).std()</code></td><td>n-period rolling std deviation</td></tr>
          <tr><td className="backtest-params-label"><code>.shift(n)</code></td><td>Shift series by n periods</td></tr>
          <tr><td className="backtest-params-label"><code>np.where(cond, a, b)</code></td><td>Conditional (if cond then a else b)</td></tr>
          <tr><td className="backtest-params-label"><code>np.log()</code></td><td>Natural logarithm</td></tr>
        </tbody>
      </table>
      <h4 style={{ marginTop: '12px' }}>Examples</h4>
      <div className="signal-builder-help-examples">
        <code>df['long_asset'].pct_change(30)</code>
        <code>df['long_asset'].rolling(50).mean() - df['long_asset'].rolling(200).mean()</code>
        <code>df['long_asset'].pct_change(252) / df['long_asset'].pct_change(252).rolling(252).std()</code>
      </div>
    </div>
  );
}

// Expression helper snippets for quick insertion
const EXPR_HELPERS = [
  { label: '.pct_change()', snippet: '.pct_change()', tip: 'Percentage change between current and prior element. Pass period as argument e.g. .pct_change(30)' },
  { label: '.diff()', snippet: '.diff()', tip: 'Absolute difference between current and prior element. Pass period as argument e.g. .diff(5)' },
  { label: '.shift()', snippet: '.shift()', tip: 'Shift values forward/backward by N periods. Positive = lag, negative = lead' },
  { label: '.rolling()', snippet: '.rolling()', tip: 'Create a rolling window. Chain with .mean(), .std() etc. e.g. .rolling(20).mean()' },
  { label: '.expanding()', snippet: '.expanding()', tip: 'Expanding window from start of series. Chain with .mean(), .std() etc.' },
  { label: '.ewm(span=)', snippet: '.ewm(span=)', tip: 'Exponentially weighted window. Span sets the decay. Chain with .mean() e.g. .ewm(span=20).mean()' },
  { label: '.mean()', snippet: '.mean()', tip: 'Average value. Use after .rolling() or .expanding()' },
  { label: '.std()', snippet: '.std()', tip: 'Standard deviation. Use after .rolling() or .expanding() for volatility' },
  { label: '.max()', snippet: '.max()', tip: 'Maximum value in window. Use after .rolling() for rolling highs' },
  { label: '.min()', snippet: '.min()', tip: 'Minimum value in window. Use after .rolling() for rolling lows' },
  { label: '.sum()', snippet: '.sum()', tip: 'Sum of values in window. Use after .rolling() or .expanding()' },
  { label: '.corr()', snippet: '.corr()', tip: 'Correlation with another series. Use after .rolling() e.g. .rolling(60).corr(other_series)' },
  { label: '.median()', snippet: '.median()', tip: 'Median value in window. More robust to outliers than .mean()' },
  { label: '.quantile()', snippet: '.quantile()', tip: 'Value at given quantile (0-1). e.g. .rolling(252).quantile(0.75)' },
  { label: '.rank(pct)', snippet: '.rank(pct=True)', tip: 'Percentile rank of each value (0 to 1). Useful for cross-sectional signals' },
  { label: '.clip()', snippet: '.clip(, )', tip: 'Cap values at lower and upper bounds. e.g. .clip(-1, 1)' },
  { label: '.fillna()', snippet: '.fillna(0)', tip: 'Replace NaN values with specified value' },
  { label: '.cumsum()', snippet: '.cumsum()', tip: 'Cumulative sum. Useful for running totals of returns' },
  { label: '.replace()', snippet: '.replace()', tip: 'Replace specific values. e.g. .replace(0, np.nan)' },
  { label: '.apply()', snippet: '.apply(lambda x: )', tip: 'Apply a custom function to each element or window' },
  { label: 'abs()', snippet: 'abs()', tip: 'Absolute value of a series' },
  { label: 'np.log()', snippet: 'np.log()', tip: 'Natural logarithm. Useful for log returns: np.log(price / price.shift(1))' },
  { label: 'np.sqrt()', snippet: 'np.sqrt()', tip: 'Square root. Common for annualising volatility: std * np.sqrt(252)' },
  { label: 'np.sign()', snippet: 'np.sign()', tip: 'Returns -1, 0, or +1 for negative, zero, or positive values' },
  { label: 'np.where()', snippet: 'np.where(, , )', tip: 'Conditional: np.where(condition, value_if_true, value_if_false)' },
  { label: 'np.maximum()', snippet: 'np.maximum(, )', tip: 'Element-wise maximum of two arrays. e.g. np.maximum(series_a, 0)' },
  { label: 'np.minimum()', snippet: 'np.minimum(, )', tip: 'Element-wise minimum of two arrays. e.g. np.minimum(series_a, 1)' },
  { label: 'np.abs()', snippet: 'np.abs()', tip: 'Element-wise absolute value (numpy version)' },
  { label: 'np.exp()', snippet: 'np.exp()', tip: 'Exponential function. Inverse of np.log()' },
];

function ExpressionHelperButtons({ onInsert }) {
  return (
    <div className="signal-builder-expr-helpers">
      {EXPR_HELPERS.map(h => (
        <button key={h.label} type="button" className="signal-builder-helper-btn"
          onClick={() => onInsert(h.snippet)} title={h.tip}>
          {h.label}
        </button>
      ))}
    </div>
  );
}

function NpSelectBuilder({ onInsert }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([{ condition: '', choice: '' }]);
  const [defaultVal, setDefaultVal] = useState('0');

  const addRow = () => setRows(prev => [...prev, { condition: '', choice: '' }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, j) => j !== i));
  const updateRow = (i, field, value) => {
    setRows(prev => { const n = [...prev]; n[i] = { ...n[i], [field]: value }; return n; });
  };

  const generate = () => {
    const validRows = rows.filter(r => r.condition && r.choice);
    if (validRows.length === 0) return;
    const conditions = validRows.map(r => r.condition).join(', ');
    const choices = validRows.map(r => r.choice).join(', ');
    const expr = `np.select([${conditions}], [${choices}], default=${defaultVal})`;
    onInsert(expr);
    setOpen(false);
  };

  if (!open) {
    return (
      <button type="button" className="signal-builder-helper-btn select-builder-toggle"
        onClick={() => setOpen(true)} title="Build an np.select() expression with multiple conditions and choices">
        np.select() builder
      </button>
    );
  }

  return (
    <div className="np-select-builder">
      <div className="np-select-builder-header">
        <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#072040' }}>np.select() builder</span>
        <button className="signal-builder-btn-remove" onClick={() => setOpen(false)} style={{ width: '22px', height: '22px', fontSize: '0.8rem' }}>×</button>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="np-select-builder-row">
          <span className="np-select-builder-label">if</span>
          <input type="text" placeholder="condition e.g. df['SPX'] > df['SPX'].rolling(200).mean()"
            value={row.condition} onChange={(e) => updateRow(i, 'condition', e.target.value)} />
          <span className="np-select-builder-label">then</span>
          <input type="text" placeholder="value e.g. 1" style={{ width: '100px' }}
            value={row.choice} onChange={(e) => updateRow(i, 'choice', e.target.value)} />
          {rows.length > 1 && (
            <button className="signal-builder-btn-remove" onClick={() => removeRow(i)}
              style={{ width: '22px', height: '22px', fontSize: '0.8rem' }}>×</button>
          )}
        </div>
      ))}
      <div className="np-select-builder-row">
        <span className="np-select-builder-label">else</span>
        <input type="text" placeholder="default value" value={defaultVal}
          onChange={(e) => setDefaultVal(e.target.value)} style={{ width: '100px' }} />
      </div>
      <div className="np-select-builder-actions">
        <button className="backtest-btn-config" type="button" onClick={addRow}>+ Add Condition</button>
        <button className="backtest-btn backtest-btn-run" type="button" onClick={generate}
          style={{ padding: '4px 12px', fontSize: '0.8rem' }}>Insert Expression</button>
      </div>
    </div>
  );
}

function parseConfigForForm(configData, parsedJson) {
  /** Convert a fetched config into the form state shape for the SignalBuilderModal. */
  const c = parsedJson || {};
  const dataSrc = c.data_source_config || {};
  const sigCfg = c.signal_config || {};
  const transCfg = c.transformation_config || {};

  // Extract signal expression — try multiple locations
  let expression = sigCfg.expression || '';
  let customExpressions = sigCfg.custom_expressions || null;
  if (!expression) {
    const sigType = sigCfg.signal_type || '';
    const nested = sigCfg[sigType];
    if (nested && typeof nested === 'object') {
      expression = nested.expression || '';
      if (!customExpressions) customExpressions = nested.custom_expressions || null;
    }
  }
  // Also check top-level custom key
  if (!expression && sigCfg.custom?.expression) {
    expression = sigCfg.custom.expression;
    if (!customExpressions) customExpressions = sigCfg.custom.custom_expressions || null;
  }

  // Transformation params
  const transType = transCfg.transformation_type || configData.transformation_type || 'select';
  const transParams = { ...transCfg };
  delete transParams.transformation_type;

  return {
    signal_name: configData.signal_name || '',
    signal_type: configData.signal_type || 'momentum',
    frequency: c.frequency || 'D',
    tickers: dataSrc.tickers || c.tickers || configData.tickers || '',
    long_asset: dataSrc.long_asset || c.long_asset || '',
    short_asset: dataSrc.short_asset || c.short_asset || '',
    signal_expression: expression,
    custom_expressions: customExpressions,
    transformation_type: transType,
    transformation_params: transParams,
    analysis_start: c.analysis_start || '1990-02-01',
    analysis_end: c.analysis_end || new Date().toISOString().split('T')[0],
    lookback_window: c.lookback_window ?? 1,
    trade_cost: c.trade_cost ?? 0.0001,
    trade_period: c.trade_period ?? 1,
    analysis_rolling_window: c.analysis_rolling_window || null,
  };
}

function SignalBuilderModal({ onClose, onSuccess, availableTickers, initialConfig, getTickerLabel, mode = 'new' }) {
  const defaultForm = {
    signal_name: '',
    signal_type: 'momentum',
    frequency: 'D',
    tickers: '',
    long_asset: '',
    short_asset: '',
    signal_expression: '',
    custom_expressions: {},
    transformation_type: 'select',
    transformation_params: { select: { conditions: ['signal > 0'], choices: [1], default: 0 } },
    analysis_start: '1990-02-01',
    analysis_end: new Date().toISOString().split('T')[0],
    lookback_window: 1,
    trade_cost: 0.0001,
    trade_period: 1,
    analysis_rolling_window: null,
  };

  const [form, setForm] = useState(initialConfig || defaultForm);

  // Initialise custom expression rows from initialConfig
  const initExprRows = initialConfig?.custom_expressions
    ? Object.entries(initialConfig.custom_expressions).map(([name, expression]) => ({ name, expression }))
    : [];
  const [customExprRows, setCustomExprRows] = useState(initExprRows);
  const [showHelp, setShowHelp] = useState(false);
  const [showTransformPreview, setShowTransformPreview] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const signalExprRef = useRef(null);
  const activeExprRef = useRef(null);
  const exprRefs = useRef([]);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Per-asset-class trade cost defaults, keyed by long asset (leg). Lets a new signal
  // prefill its trade cost the moment its long asset is chosen.
  const [legCosts, setLegCosts] = useState({});
  useEffect(() => {
    api.get('/api/tactical/trade-costs').then(r => setLegCosts(r.data.leg_costs || {})).catch(() => {});
  }, []);
  const handleLongChange = (v) => setForm(prev => {
    const next = { ...prev, long_asset: v };
    const lc = legCosts[v];
    if (lc != null && Math.abs((prev.trade_cost ?? 0.0001) - 0.0001) < 1e-9) next.trade_cost = lc;
    return next;
  });

  const handleInsert = (snippet) => {
    const el = activeExprRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = el.value.substring(0, start) + snippet + el.value.substring(end);

    if (el === signalExprRef.current) {
      updateForm('signal_expression', newVal);
    } else {
      const idx = exprRefs.current.findIndex(r => r === el);
      if (idx >= 0) handleCustomExprChange(idx, 'expression', newVal);
    }

    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + snippet.length;
      el.focus();
    });
  };

  const handleAddCustomExpr = () => {
    setCustomExprRows(prev => [...prev, { name: '', expression: '' }]);
  };

  const handleCustomExprChange = (index, field, value) => {
    const newRows = [...customExprRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setCustomExprRows(newRows);

    // Update form
    const exprs = {};
    newRows.forEach(r => { if (r.name && r.expression) exprs[r.name] = r.expression; });
    updateForm('custom_expressions', Object.keys(exprs).length > 0 ? exprs : null);
  };

  const handleRemoveCustomExpr = (index) => {
    const newRows = customExprRows.filter((_, i) => i !== index);
    setCustomExprRows(newRows);
    exprRefs.current.splice(index, 1);
    const exprs = {};
    newRows.forEach(r => { if (r.name && r.expression) exprs[r.name] = r.expression; });
    updateForm('custom_expressions', Object.keys(exprs).length > 0 ? exprs : null);
  };

  const handleRunBacktest = async () => {
    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const payload = { ...form };
      // Clean up nulls
      if (!payload.custom_expressions || Object.keys(payload.custom_expressions).length === 0) {
        payload.custom_expressions = null;
      }
      if (!payload.analysis_rolling_window) {
        payload.analysis_rolling_window = null;
      }

      const res = await api.post('/api/tactical/backtest', payload);
      if (res.data.success) {
        setResult(res.data);
      } else {
        setError(res.data.detail || 'Backtest failed');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(detail);
    } finally {
      setRunning(false);
    }
  };

  const handleSave = () => {
    // Result is already saved during backtest — just close and refresh
    onSuccess();
    onClose();
  };

  const formatPercent = (v) => v != null ? (v * 100).toFixed(2) + '%' : '-';
  const formatNum = (v) => v != null ? v.toFixed(4) : '-';

  return (
    <div className="backtest-modal-overlay">
      <div className="signal-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>{mode === 'edit' ? 'Edit Signal Backtest' : mode === 'copy' ? 'Copy Signal Backtest' : 'New Signal Backtest'}</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="signal-builder-body">
          {/* Section 1: Metadata */}
          <div className="signal-builder-section">
            <h3>Signal Metadata</h3>
            <div className="signal-builder-field-row">
              <div className="signal-builder-field" style={{ flex: 2 }}>
                <label>Signal Name *</label>
                <input type="text" value={form.signal_name} onChange={(e) => updateForm('signal_name', e.target.value)}
                  placeholder="e.g. World_Equity_Momentum_30d" />
              </div>
              <div className="signal-builder-field">
                <label>Signal Type *</label>
                <input
                  type="text"
                  list="signal-type-options"
                  value={form.signal_type}
                  onChange={(e) => updateForm('signal_type', e.target.value)}
                  placeholder="Select or type a new type"
                />
                <datalist id="signal-type-options">
                  {SIGNAL_TYPES.map(t => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div className="signal-builder-field" style={{ width: '80px' }}>
                <label>Frequency</label>
                <select value={form.frequency} onChange={(e) => updateForm('frequency', e.target.value)}>
                  <option value="D">Daily</option>
                  <option value="M">Monthly</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Data Source */}
          <div className="signal-builder-section">
            <h3>Data Source</h3>
            <div className="signal-builder-field">
              <label>Tickers * <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#6c757d' }}>— click L / S on each ticker to assign long and short assets</span></label>
              <TickerSelector
                availableTickers={availableTickers}
                selectedTickers={form.tickers}
                onChange={(v) => updateForm('tickers', v)}
                freq={form.frequency}
                getTickerLabel={getTickerLabel}
                longAsset={form.long_asset}
                shortAsset={form.short_asset}
                onLongChange={handleLongChange}
                onShortChange={(v) => updateForm('short_asset', v)}
              />
            </div>
          </div>

          {/* Section 3: Signal Expression */}
          <div className="signal-builder-section">
            <h3>
              Signal Expression
              <button className="backtest-btn-config" style={{ marginLeft: '12px', fontSize: '0.7rem' }}
                onClick={() => setShowHelp(!showHelp)}>
                {showHelp ? 'Hide Help' : 'Show Help'}
              </button>
            </h3>
            <ExpressionHelp show={showHelp} />

            {/* Shared toolbar */}
            <div className="signal-builder-expr-toolbar">
              <div className="signal-builder-expr-helpers">
                {parseTickers(form.tickers).map(t => (
                  <button key={t} type="button" className="signal-builder-helper-btn ticker"
                    onClick={() => handleInsert(`df['${t}']`)} title={`df['${t}']`}>
                    {getTickerLabel ? getTickerLabel(t) : t}
                  </button>
                ))}
                {customExprRows.filter(r => r.name).map((r, i) => (
                  <button key={`var-${i}`} type="button" className="signal-builder-helper-btn variable"
                    onClick={() => handleInsert(`df['${r.name}']`)} title={`df['${r.name}']`}>
                    {r.name}
                  </button>
                ))}
              </div>
              <ExpressionHelperButtons onInsert={handleInsert} />
              <NpSelectBuilder onInsert={handleInsert} />
            </div>

            {/* Intermediate step cards */}
            {customExprRows.map((row, i) => (
              <div key={i} className="signal-builder-step-card">
                <div className="signal-builder-step-header">
                  <span className="signal-builder-step-label">STEP {i + 1}</span>
                  <input
                    type="text"
                    className="signal-builder-step-name-input"
                    placeholder="Variable name (e.g. momentum)"
                    value={row.name}
                    onChange={(e) => handleCustomExprChange(i, 'name', e.target.value)}
                  />
                  <button className="signal-builder-btn-remove" onClick={() => handleRemoveCustomExpr(i)}>×</button>
                </div>
                <textarea
                  ref={el => { exprRefs.current[i] = el; }}
                  onFocus={() => { activeExprRef.current = exprRefs.current[i]; }}
                  rows={2}
                  value={row.expression}
                  onChange={(e) => handleCustomExprChange(i, 'expression', e.target.value)}
                  placeholder="e.g. df['long_asset'].pct_change(252)"
                />
              </div>
            ))}

            <button className="backtest-btn-config" onClick={handleAddCustomExpr} style={{ marginBottom: '8px' }}>
              + Add Step
            </button>

            {/* Final expression card */}
            <div className="signal-builder-step-card final">
              <div className="signal-builder-step-header">
                <span className="signal-builder-step-label">FINAL SIGNAL EXPRESSION *</span>
              </div>
              <textarea
                ref={el => { signalExprRef.current = el; }}
                onFocus={() => { activeExprRef.current = signalExprRef.current; }}
                rows={3}
                value={form.signal_expression}
                onChange={(e) => updateForm('signal_expression', e.target.value)}
                placeholder="e.g. df['long_asset'].pct_change(30)"
              />
            </div>
          </div>

          {/* Section 4: Transformation */}
          <div className="signal-builder-section">
            <h3>Transformation</h3>
            <div className="signal-builder-field">
              <label>Type</label>
              <select value={form.transformation_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  updateForm('transformation_type', newType);
                  // Reset params for new type
                  if (newType === 'select') updateForm('transformation_params', { select: { conditions: ['signal > 0'], choices: [1], default: 0 } });
                  else if (newType === 'logit' || newType === 'logit_even') updateForm('transformation_params', { lower_bound: -1, upper_bound: 1, k_parameter: 1, standardisation_window: 0 });
                  else if (newType === 'linear') updateForm('transformation_params', { lower_bound: -1, upper_bound: 1, standardisation_window: 0 });
                  else if (newType === 'percentile') updateForm('transformation_params', { lower_bound: -1, upper_bound: 1, standardisation_window: 0 });
                  else if (newType === 'quantile') updateForm('transformation_params', { lower_bound: -1, upper_bound: 1, buckets: 5, standardisation_window: 0 });
                  else if (newType === 'custom') updateForm('transformation_params', { custom: { expression: '' } });
                }}>
                {TRANSFORMATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <TransformationConfig
              type={form.transformation_type}
              params={form.transformation_params}
              onChange={(v) => updateForm('transformation_params', v)}
            />
            {form.transformation_type !== 'custom' && (
              <>
                <button
                  type="button"
                  className="backtest-btn-config"
                  style={{ marginTop: '8px' }}
                  onClick={() => setShowTransformPreview(prev => !prev)}
                >
                  {showTransformPreview ? 'Hide Transform' : 'Show Transform'}
                </button>
                {showTransformPreview && (
                  <TransformPreview type={form.transformation_type} params={form.transformation_params} />
                )}
              </>
            )}
          </div>

          {/* Section 5: Analysis Settings */}
          <div className="signal-builder-section">
            <h3>Analysis Settings</h3>
            <div className="signal-builder-field-row">
              <div className="signal-builder-field">
                <label>Start Date</label>
                <input type="date" value={form.analysis_start} onChange={(e) => updateForm('analysis_start', e.target.value)} />
              </div>
              <div className="signal-builder-field">
                <label>End Date</label>
                <input type="date" value={form.analysis_end} onChange={(e) => updateForm('analysis_end', e.target.value)} />
              </div>
            </div>
            <div className="signal-builder-field-row">
              <div className="signal-builder-field">
                <label>Lookback Window</label>
                <input type="number" value={form.lookback_window} onChange={(e) => updateForm('lookback_window', parseInt(e.target.value) || 1)} />
              </div>
              <div className="signal-builder-field">
                <label>Trade Cost</label>
                <input type="number" step="0.0001" value={form.trade_cost} onChange={(e) => updateForm('trade_cost', parseFloat(e.target.value) || 0)} />
              </div>
              <div className="signal-builder-field">
                <label>Trade Period</label>
                <input type="number" value={form.trade_period} onChange={(e) => updateForm('trade_period', parseInt(e.target.value) || 1)} />
              </div>
              <div className="signal-builder-field">
                <label>Rolling Window</label>
                <input type="number" value={form.analysis_rolling_window || ''}
                  onChange={(e) => updateForm('analysis_rolling_window', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Auto" />
              </div>
            </div>
          </div>

          {/* Section 6: Actions & Results */}
          <div className="signal-builder-section">
            {error && <div className="backtest-error" style={{ marginBottom: '12px' }}>{error}</div>}

            {!result ? (
              <button
                className="backtest-btn backtest-btn-primary"
                onClick={handleRunBacktest}
                disabled={running || !form.signal_name || !form.tickers || !form.long_asset || !form.short_asset || !form.signal_expression}
                style={{ width: 'auto', padding: '12px 32px' }}
              >
                {running ? 'Running Backtest...' : 'Run Backtest'}
              </button>
            ) : (
              <div className="signal-builder-results">
                <h3>Results Preview</h3>
                <div className="signal-builder-results-grid">
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Annualised Return</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Annualised Return']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Information Ratio</span>
                    <span className="signal-builder-metric-value">{formatNum(result.results?.['Information Ratio']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">True Alpha</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['True Alpha']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Hit Rate</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Hit Rate']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Max Drawdown</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Max Drawdown']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Tracking Error</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Tracking Error']?.Current)}</span>
                  </div>
                </div>

                {/* Mini chart */}
                {result.stream_preview && result.stream_preview.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={result.stream_preview}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E6E6" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => v != null ? (v * 100).toFixed(1) + '%' : '-'} labelFormatter={(l) => new Date(l).toLocaleDateString('en-AU')} />
                        <Line type="monotone" dataKey="strategy_return_cumulative" name="Strategy" stroke="#072040" dot={false} strokeWidth={2} isAnimationActive={false} />
                        <Line type="monotone" dataKey="benchmark_return_cumulative" name="Benchmark" stroke="#f0a500" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                  <button className="backtest-btn backtest-btn-primary" onClick={handleSave} style={{ width: 'auto', padding: '10px 24px' }}>
                    Done — View in Dashboard
                  </button>
                  <button className="backtest-btn backtest-btn-secondary" onClick={() => { setResult(null); setError(null); }} style={{ width: 'auto', padding: '10px 24px' }}>
                    Modify
                  </button>
                  <button className="backtest-btn backtest-btn-primary" onClick={() => { setResult(null); setError(null); setTimeout(handleRunBacktest, 0); }} style={{ width: 'auto', padding: '10px 24px' }}>
                    Re-run
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============= Batch Upload Modal =============
const BATCH_TEMPLATE = {
  defaults: {
    tickers: "MSWRLDL(RI),BMUS10Y(RI)",
    long_asset: "MSWRLDL(RI)",
    short_asset: "BMUS10Y(RI)",
    frequency: "D",
    analysis_start: "2000-01-01",
    transformation_type: "linear"
  },
  signals: [
    {
      signal_name: "Fast_Mom_60d",
      signal_type: "momentum",
      signal_expression: "df['long_asset'].pct_change(60)"
    },
    {
      signal_name: "Slow_Mom_252d",
      signal_type: "momentum",
      signal_expression: "df['long_asset'].pct_change(252)",
      transformation_type: "logit"
    }
  ]
};

function BatchUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = () => {
    const blob = new Blob([JSON.stringify(BATCH_TEMPLATE, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_backtest_template.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validateAndParse = (text, fileName) => {
    setParseError(null);
    setParsedData(null);
    setResults(null);

    // Size check (~500KB)
    if (text.length > 512000) {
      setParseError('File exceeds 500KB limit.');
      return;
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      setParseError('Invalid JSON — check syntax.');
      return;
    }

    if (!data.signals || !Array.isArray(data.signals)) {
      setParseError('Missing "signals" array.');
      return;
    }
    if (data.signals.length === 0) {
      setParseError('Signals array is empty.');
      return;
    }
    if (data.signals.length > 20) {
      setParseError(`Too many signals (${data.signals.length}). Maximum is 20.`);
      return;
    }

    // Validate each signal has required fields
    for (let i = 0; i < data.signals.length; i++) {
      const s = data.signals[i];
      if (!s.signal_name) {
        setParseError(`Signal ${i + 1}: missing "signal_name".`);
        return;
      }
      if (!s.signal_expression) {
        setParseError(`Signal ${i + 1} ("${s.signal_name}"): missing "signal_expression".`);
        return;
      }
    }

    // Check duplicates
    const names = data.signals.map(s => s.signal_name);
    const dupes = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupes.length > 0) {
      setParseError(`Duplicate signal name: "${dupes[0]}".`);
      return;
    }

    setFile({ name: fileName });
    setParsedData(data);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => validateAndParse(ev.target.result, f.name);
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => validateAndParse(ev.target.result, f.name);
    reader.readAsText(f);
  };

  const handleRunBatch = async () => {
    if (!parsedData) return;
    setRunning(true);
    setResults(null);
    try {
      const res = await api.post('/api/tactical/backtest/batch', parsedData);
      setResults(res.data);
      if (res.data.completed > 0 && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setResults({ success: false, total: 0, completed: 0, failed: 0, results: [], _error: detail });
    } finally {
      setRunning(false);
    }
  };

  const fmtPct = (v) => v != null ? (v * 100).toFixed(2) + '%' : '—';
  const fmtNum = (v) => v != null ? Number(v).toFixed(2) : '—';

  return (
    <div className="backtest-modal-overlay">
      <div className="signal-builder-modal" style={{ maxWidth: '950px' }}>
        <div className="backtest-modal-header">
          <h2>Batch Upload Backtests</h2>
          <button className="backtest-modal-close" onClick={onClose} disabled={running}>&times;</button>
        </div>
        <div className="backtest-modal-body">

          {/* Download template + file drop zone */}
          <div style={{ marginBottom: 16, display: 'flex', gap: 10 }}>
            <button className="batch-upload-template-btn" onClick={handleDownloadTemplate}>
              Download Template
            </button>
          </div>

          <div
            className={`batch-upload-file-area${file ? ' has-file' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {file ? (
              <p className="file-name">{file.name} — {parsedData?.signals?.length} signal{parsedData?.signals?.length !== 1 ? 's' : ''} loaded</p>
            ) : (
              <p>Drop a JSON file here or click to browse</p>
            )}
          </div>

          {parseError && <div className="backtest-error" style={{ marginBottom: 16 }}>{parseError}</div>}

          {/* Preview table */}
          {parsedData && !results && (
            <>
              <table className="batch-upload-signals-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Signal Name</th>
                    <th>Type</th>
                    <th>Expression</th>
                    <th>Transform</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.signals.map((s, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{s.signal_name}</td>
                      <td>{s.signal_type || parsedData.defaults?.signal_type || '—'}</td>
                      <td className="expr-cell" title={s.signal_expression}>{s.signal_expression}</td>
                      <td>{s.transformation_type || parsedData.defaults?.transformation_type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="batch-upload-actions">
                <button
                  className="batch-upload-run-btn"
                  onClick={handleRunBatch}
                  disabled={running}
                >
                  {running ? 'Running...' : `Run ${parsedData.signals.length} Backtest${parsedData.signals.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}

          {/* Results */}
          {results && (
            <>
              {results._error ? (
                <div className="backtest-error">{results._error}</div>
              ) : (
                <>
                  <div className="batch-summary">
                    <span>
                      <span className="batch-completed">{results.completed} completed</span>
                      {results.failed > 0 && <>, <span className="batch-failed">{results.failed} failed</span></>}
                      {' '}of {results.total} signals
                    </span>
                  </div>

                  <table className="batch-upload-signals-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Signal Name</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Ann. Return</th>
                        <th style={{ textAlign: 'right' }}>IR</th>
                        <th style={{ textAlign: 'right' }}>Hit Rate</th>
                        <th style={{ textAlign: 'right' }}>Max DD</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((r, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{r.signal_name}</td>
                          <td>
                            <span className={r.success ? 'batch-result-success' : 'batch-result-failed'}>
                              {r.success ? 'Pass' : 'Fail'}
                            </span>
                          </td>
                          <td className="batch-metric-cell">{r.success ? fmtPct(r.annualised_return) : '—'}</td>
                          <td className="batch-metric-cell">{r.success ? fmtNum(r.information_ratio) : '—'}</td>
                          <td className="batch-metric-cell">{r.success ? fmtPct(r.hit_rate) : '—'}</td>
                          <td className="batch-metric-cell">{r.success ? fmtPct(r.max_drawdown) : '—'}</td>
                          <td className="batch-error-cell" title={r.error || ''}>{r.error || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="batch-upload-actions" style={{ marginTop: 16 }}>
                <button className="batch-upload-template-btn" onClick={() => {
                  setFile(null);
                  setParsedData(null);
                  setResults(null);
                  setParseError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}>
                  Upload Another
                </button>
                <button className="batch-upload-run-btn" onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============= Ticker Labels Modal =============
const CLASSIFICATION_SUGGESTIONS = [
  'Equity', 'Alternatives', 'Credit', 'FX',
  '2Y Bonds', '10Y Bonds', '30Y Bonds', 'Inflation-Linked Bonds',
  'Commodities', 'Valuation', 'Earnings',
  'Macro/Asia', 'Macro/Europe', 'Macro/Australia', 'Macro/USA',
  'Growth Barometer'
];

function DataConfigSection({ availableTickers, tickerLabels, onSave, onMetadataSave }) {
  const [localLabels, setLocalLabels] = useState({ ...tickerLabels });
  const existingMetadata = availableTickers?.ticker_metadata || {};
  const [localMetadata, setLocalMetadata] = useState({ ...existingMetadata });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [configTab, setConfigTab] = useState('All');
  const [modalRegionFilter, setModalRegionFilter] = useState('');
  const [modalTypeFilter, setModalTypeFilter] = useState('');
  const [modalStyleFilter, setModalStyleFilter] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { ticker, signals, loading }

  // Reset local state when availableTickers or tickerLabels change externally
  useEffect(() => {
    setLocalLabels({ ...tickerLabels });
  }, [tickerLabels]);
  useEffect(() => {
    setLocalMetadata({ ...(availableTickers?.ticker_metadata || {}) });
  }, [availableTickers]);

  // Merge and deduplicate tickers from daily + monthly, track frequency
  const tickerInfo = useMemo(() => {
    const daily = new Set(availableTickers?.daily_tickers || []);
    const monthly = new Set(availableTickers?.monthly_tickers || []);
    const allTickers = new Set([...daily, ...monthly]);
    return [...allTickers].sort().map(ticker => ({
      ticker,
      freq: daily.has(ticker) && monthly.has(ticker) ? 'both'
        : daily.has(ticker) ? 'daily' : 'monthly'
    }));
  }, [availableTickers]);

  // Build classification tabs from current local metadata (live as you edit)
  const classificationTabs = useMemo(() => {
    const groups = { 'All': 0, 'Unclassified': 0 };
    tickerInfo.forEach(({ ticker }) => {
      const cls = localMetadata[ticker]?.asset_class || null;
      groups['All']++;
      if (cls) {
        groups[cls] = (groups[cls] || 0) + 1;
      } else {
        groups['Unclassified']++;
      }
    });
    const sorted = Object.keys(groups).sort((a, b) => {
      if (a === 'All') return -1;
      if (b === 'All') return 1;
      if (a === 'Unclassified') return 1;
      if (b === 'Unclassified') return -1;
      return a.localeCompare(b);
    });
    return sorted.map(name => ({ name, count: groups[name] }));
  }, [tickerInfo, localMetadata]);

  // Unique values for dropdown filters
  const modalFilterOptions = useMemo(() => {
    const regions = new Set(), types = new Set(), styles = new Set();
    tickerInfo.forEach(({ ticker }) => {
      const m = localMetadata[ticker];
      if (m?.region) regions.add(m.region);
      if (m?.type) types.add(m.type);
      if (m?.style) styles.add(m.style);
    });
    return {
      regions: [...regions].sort(),
      types: [...types].sort(),
      styles: [...styles].sort(),
    };
  }, [tickerInfo, localMetadata]);

  // Filter by active tab, then by dropdowns, then by search
  const filtered = useMemo(() => {
    let list = tickerInfo;
    if (configTab !== 'All') {
      list = list.filter(({ ticker }) => {
        const cls = localMetadata[ticker]?.asset_class || null;
        return configTab === 'Unclassified' ? !cls : cls === configTab;
      });
    }
    if (modalRegionFilter) {
      list = list.filter(({ ticker }) => (localMetadata[ticker]?.region || '') === modalRegionFilter);
    }
    if (modalTypeFilter) {
      list = list.filter(({ ticker }) => (localMetadata[ticker]?.type || '') === modalTypeFilter);
    }
    if (modalStyleFilter) {
      list = list.filter(({ ticker }) => (localMetadata[ticker]?.style || '') === modalStyleFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(({ ticker }) =>
        ticker.toLowerCase().includes(q) ||
        (localLabels[ticker] || '').toLowerCase().includes(q) ||
        (localMetadata[ticker]?.asset_class || '').toLowerCase().includes(q) ||
        (localMetadata[ticker]?.region || '').toLowerCase().includes(q) ||
        (localMetadata[ticker]?.type || '').toLowerCase().includes(q) ||
        (localMetadata[ticker]?.style || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [tickerInfo, configTab, search, localLabels, localMetadata, modalRegionFilter, modalTypeFilter, modalStyleFilter]);

  const updateMetaField = (ticker, field, value) => {
    setLocalMetadata(prev => ({
      ...prev,
      [ticker]: { ...prev[ticker], [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      // Save labels
      await api.post('/api/tactical/ticker-labels', { labels: localLabels });
      // Save metadata
      await api.post('/api/tactical/ticker-metadata', { metadata: localMetadata });
      // Re-fetch available tickers (includes labels in metadata)
      const tickersRes = await api.get('/api/tactical/available-tickers');
      const meta = tickersRes.data?.ticker_metadata || {};
      const freshLabels = {};
      Object.entries(meta).forEach(([ticker, m]) => {
        if (m.label) freshLabels[ticker] = m.label;
      });
      onSave(freshLabels);
      if (onMetadataSave) onMetadataSave(tickersRes.data);
      setSaveMsg('Saved successfully');
    } catch (err) {
      console.error('Error saving ticker labels/metadata:', err);
      setSaveMsg('Error saving — please try again');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async (ticker) => {
    setDeleteConfirm({ ticker, signals: [], loading: true });
    try {
      const res = await api.get(`/api/tactical/ticker-usage/${encodeURIComponent(ticker)}`);
      setDeleteConfirm({ ticker, ...res.data, loading: false });
    } catch (err) {
      console.error('Error checking ticker usage:', err);
      setDeleteConfirm({ ticker, signals: [], is_used: false, signal_count: 0, loading: false });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    const { ticker } = deleteConfirm;
    try {
      await api.delete(`/api/tactical/ticker-data/${encodeURIComponent(ticker)}`);
      // Re-fetch available tickers
      const tickersRes = await api.get('/api/tactical/available-tickers');
      const meta = tickersRes.data?.ticker_metadata || {};
      const freshLabels = {};
      Object.entries(meta).forEach(([t, m]) => {
        if (m.label) freshLabels[t] = m.label;
      });
      onSave(freshLabels);
      if (onMetadataSave) onMetadataSave(tickersRes.data);
      // Clean up local state
      setLocalLabels(prev => { const next = { ...prev }; delete next[ticker]; return next; });
      setLocalMetadata(prev => { const next = { ...prev }; delete next[ticker]; return next; });
      setDeleteConfirm(null);
      setSaveMsg(`Deleted ${ticker}`);
    } catch (err) {
      console.error('Error deleting ticker:', err);
      setSaveMsg(`Error deleting ${ticker}`);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="data-config-section">
      <datalist id="classification-suggestions">
        {CLASSIFICATION_SUGGESTIONS.map(c => <option key={c} value={c} />)}
      </datalist>

      {/* Classification tabs */}
      <div className="market-data-classification-tabs" style={{ marginBottom: '12px' }}>
        {classificationTabs.map(tab => (
          <button
            key={tab.name}
            className={`market-data-classification-tab${configTab === tab.name ? ' active' : ''}`}
            onClick={() => setConfigTab(tab.name)}
          >
            {tab.name}
            <span className="market-data-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <select
          className="market-data-filter-select"
          value={modalRegionFilter}
          onChange={(e) => setModalRegionFilter(e.target.value)}
        >
          <option value="">All Regions</option>
          {modalFilterOptions.regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="market-data-filter-select"
          value={modalTypeFilter}
          onChange={(e) => setModalTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          {modalFilterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          className="market-data-filter-select"
          value={modalStyleFilter}
          onChange={(e) => setModalStyleFilter(e.target.value)}
        >
          <option value="">All Styles</option>
          {modalFilterOptions.styles.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <input
        type="text"
        className="ticker-labels-search"
        placeholder="Search tickers, labels, or classification..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <table className="ticker-labels-table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Freq</th>
              <th>Label</th>
              <th>Classification</th>
              <th>Region</th>
              <th>Type</th>
              <th>Style</th>
              <th style={{ width: '60px', textAlign: 'center' }}>Index</th>
              <th style={{ width: '60px', textAlign: 'center', color: '#dc3545' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(({ ticker, freq }) => (
              <tr key={ticker}>
                <td>{ticker}</td>
                <td><span className="ticker-labels-freq">{freq}</span></td>
                <td>
                  <CommitInput
                    type="text"
                    placeholder="Enter display name..."
                    value={localLabels[ticker] || ''}
                    onCommit={(v) => setLocalLabels(prev => ({ ...prev, [ticker]: v }))}
                  />
                </td>
                <td>
                  <CommitInput
                    type="text"
                    list="classification-suggestions"
                    placeholder="e.g. Equity, FX..."
                    value={localMetadata[ticker]?.asset_class || ''}
                    onCommit={(v) => updateMetaField(ticker, 'asset_class', v || null)}
                  />
                </td>
                <td>
                  <CommitInput
                    type="text"
                    placeholder="Region..."
                    value={localMetadata[ticker]?.region || ''}
                    onCommit={(v) => updateMetaField(ticker, 'region', v || null)}
                  />
                </td>
                <td>
                  <CommitInput
                    type="text"
                    placeholder="Type..."
                    value={localMetadata[ticker]?.type || ''}
                    onCommit={(v) => updateMetaField(ticker, 'type', v || null)}
                  />
                </td>
                <td>
                  <CommitInput
                    type="text"
                    placeholder="Style..."
                    value={localMetadata[ticker]?.style || ''}
                    onCommit={(v) => updateMetaField(ticker, 'style', v || null)}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={localMetadata[ticker]?.is_index === true}
                    onChange={(e) => updateMetaField(ticker, 'is_index', e.target.checked)}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="data-config-delete-btn"
                    title="Delete ticker data"
                    onClick={() => handleDeleteClick(ticker)}
                  >✕</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center', color: '#6c757d' }}>No tickers found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '16px', alignItems: 'center' }}>
        <button className="backtest-btn backtest-btn-primary" style={{ width: 'auto' }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {saveMsg && (
          <span style={{ fontSize: '0.85rem', color: saveMsg.startsWith('Error') ? '#dc3545' : '#91CF9B' }}>
            {saveMsg}
          </span>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="backtest-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="backtest-modal-content" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="backtest-modal-header">
              <h2>Delete Ticker: {deleteConfirm.ticker}</h2>
              <button className="backtest-modal-close" onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div className="backtest-modal-body">
              {deleteConfirm.loading ? (
                <div style={{ padding: '16px', textAlign: 'center', color: '#6c757d' }}>Checking usage...</div>
              ) : (
                <>
                  {deleteConfirm.is_used ? (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', marginBottom: '12px', fontSize: '0.85rem' }}>
                        This ticker is used in <strong>{deleteConfirm.signal_count}</strong> signal{deleteConfirm.signal_count !== 1 ? 's' : ''}. Deleting it will not remove the signals but the data will no longer be available.
                      </div>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <table className="ticker-labels-table" style={{ fontSize: '0.8rem' }}>
                          <thead>
                            <tr>
                              <th>Signal</th>
                              <th>Long Asset</th>
                              <th>Short Asset</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deleteConfirm.signals.map((s, i) => (
                              <tr key={i}>
                                <td>{s.signal_name}</td>
                                <td>{s.long_asset}</td>
                                <td>{s.short_asset}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p style={{ marginBottom: '16px', fontSize: '0.9rem' }}>
                      This ticker is not used in any signals. Are you sure you want to delete all data for <strong>{deleteConfirm.ticker}</strong>?
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button className="backtest-btn backtest-btn-secondary" style={{ width: 'auto' }} onClick={() => setDeleteConfirm(null)}>
                      Cancel
                    </button>
                    <button className="backtest-btn-delete" style={{ padding: '8px 20px' }} onClick={handleDeleteConfirm}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============= Combinations Table =============
function CombinationsTable({ combinations, selectedIds, loading, onSelect, onSelectAll, onViewConfig, getTickerLabel, onToggleFlag }) {
  const [sortKey, setSortKey] = useState('created_date');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedCombinations = useMemo(() => {
    return [...combinations].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [combinations, sortKey, sortDir]);

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return (value * 100).toFixed(2) + '%';
  };
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };
  const getValueClass = (value) => {
    if (value === null || value === undefined) return '';
    return value >= 0 ? 'backtest-positive' : 'backtest-negative';
  };
  const getSortClass = (key) => {
    if (sortKey !== key) return 'backtest-sortable';
    return sortDir === 'asc' ? 'backtest-sortable backtest-sorted-asc' : 'backtest-sortable backtest-sorted-desc';
  };

  const getConstituentCount = (constituents) => {
    if (!constituents) return 0;
    try {
      const parsed = typeof constituents === 'string' ? JSON.parse(constituents) : constituents;
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch { return 0; }
  };

  if (loading) {
    return (
      <div className="backtest-table-container">
        <div className="backtest-loading">Loading combinations...</div>
      </div>
    );
  }

  if (combinations.length === 0) {
    return (
      <div className="backtest-table-container">
        <div className="backtest-empty">No combinations found. Create one using "+ New Combination".</div>
      </div>
    );
  }

  return (
    <div className="backtest-table-container">
      <div className="backtest-table-wrapper">
        <table className="backtest-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedIds.size === combinations.length && combinations.length > 0}
                  onChange={onSelectAll}
                />
              </th>
              <th style={{ width: '30px' }}></th>
              <th style={{ width: '30px' }}></th>
              <th className={getSortClass('combination_name')} onClick={() => handleSort('combination_name')}>Name</th>
              <th className={getSortClass('long_asset')} onClick={() => handleSort('long_asset')}>Long Asset</th>
              <th className={getSortClass('short_asset')} onClick={() => handleSort('short_asset')}>Short Asset</th>
              <th>Constituents</th>
              <th className={getSortClass('annualised_return')} onClick={() => handleSort('annualised_return')} title="Annualised strategy return: cumulative^(365/N) − 1">Ann. Return</th>
              <th className={getSortClass('information_ratio')} onClick={() => handleSort('information_ratio')} title="Annualised active return ÷ tracking error">Info Ratio</th>
              <th className={getSortClass('true_alpha')} onClick={() => handleSort('true_alpha')} title="Geometric cumulative active return: ∏(1 + active_return) − 1">True Alpha</th>
              <th className={getSortClass('max_drawdown')} onClick={() => handleSort('max_drawdown')} title="Largest peak-to-trough decline in cumulative strategy return">Max DD</th>
              <th className={getSortClass('hit_rate')} onClick={() => handleSort('hit_rate')} title="Fraction of periods with positive strategy return">Hit Rate</th>
              <th className={getSortClass('tracking_error')} onClick={() => handleSort('tracking_error')} title="Annualised std of active returns (strategy − benchmark): √365 × σ">Track Error</th>
              <th className={getSortClass('annualised_turnover')} onClick={() => handleSort('annualised_turnover')} title="Annualised sum of absolute weight changes">Turnover</th>
              <th className={getSortClass('created_date')} onClick={() => handleSort('created_date')}>Created</th>
              <th>Config</th>
            </tr>
          </thead>
          <tbody>
            {sortedCombinations.map(combo => (
              <tr key={combo.combination_id} className={selectedIds.has(combo.combination_id) ? 'backtest-selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(combo.combination_id)}
                    onChange={() => onSelect(combo.combination_id)}
                  />
                </td>
                <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '1rem' }}
                    onClick={() => onToggleFlag(combo.combination_id, 'is_favourite', !combo.is_favourite)}
                    title={combo.is_favourite ? 'Remove favourite' : 'Mark as favourite'}>
                  {combo.is_favourite ? '\u2605' : '\u2606'}
                </td>
                <td style={{ textAlign: 'center', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
                    onClick={() => onToggleFlag(combo.combination_id, 'is_production', !combo.is_production)}
                    title={combo.is_production ? 'Remove from production' : 'Mark as production'}>
                  <span style={{
                    background: combo.is_production ? '#91CF9B' : '#E7E6E6',
                    color: combo.is_production ? '#072040' : '#6c757d',
                    padding: '2px 6px', borderRadius: '3px'
                  }}>P</span>
                </td>
                <td title={combo.combination_name}>{combo.combination_name || '-'}</td>
                <td title={combo.long_asset}>{combo.long_asset ? combo.long_asset.split(', ').map(a => getTickerLabel ? getTickerLabel(a.trim()) : a).join(', ') : '-'}</td>
                <td title={combo.short_asset}>{combo.short_asset ? combo.short_asset.split(', ').map(a => getTickerLabel ? getTickerLabel(a.trim()) : a).join(', ') : '-'}</td>
                <td>
                  <span className="combination-constituents-badge">
                    {getConstituentCount(combo.constituents)} signals
                  </span>
                </td>
                <td className={getValueClass(combo.annualised_return)}>{formatPercent(combo.annualised_return)}</td>
                <td className={getValueClass(combo.information_ratio)}>{formatNumber(combo.information_ratio)}</td>
                <td className={getValueClass(combo.true_alpha)}>{formatPercent(combo.true_alpha)}</td>
                <td className="backtest-negative">{formatPercent(combo.max_drawdown)}</td>
                <td>{formatPercent(combo.hit_rate)}</td>
                <td>{formatPercent(combo.tracking_error)}</td>
                <td>{formatNumber(combo.annualised_turnover)}</td>
                <td>{formatDate(combo.created_date)}</td>
                <td>
                  <button className="backtest-btn-config" onClick={() => onViewConfig(combo.combination_id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="backtest-table-footer">
        Showing {combinations.length} combinations | {selectedIds.size} selected
      </div>
    </div>
  );
}


// ============= Combination Builder Modal =============
function CombinationBuilderModal({ onClose, onSuccess, getTickerLabel, filterOptions }) {
  const [combinationName, setCombinationName] = useState('');
  const [eligibleSignals, setEligibleSignals] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [selectedSignals, setSelectedSignals] = useState(new Set());
  const [weights, setWeights] = useState({});
  const [tradeCost, setTradeCost] = useState(0.0001);
  const [analysisStart, setAnalysisStart] = useState('1990-02-01');
  const [analysisEnd, setAnalysisEnd] = useState(new Date().toISOString().split('T')[0]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Signal picker filters
  const [sigFilterType, setSigFilterType] = useState('');
  const [sigFilterName, setSigFilterName] = useState('');
  const [sigFilterTransform, setSigFilterTransform] = useState('');
  const [sigFilterLongAsset, setSigFilterLongAsset] = useState('');
  const [sigFilterShortAsset, setSigFilterShortAsset] = useState('');
  const [sigFilterFavourite, setSigFilterFavourite] = useState('');
  const [sigFilterProduction, setSigFilterProduction] = useState('');

  // Fetch eligible signals based on filters
  const fetchEligible = useCallback(() => {
    setLoadingEligible(true);
    const params = new URLSearchParams();
    if (sigFilterLongAsset) params.append('long_asset', sigFilterLongAsset);
    if (sigFilterShortAsset) params.append('short_asset', sigFilterShortAsset);
    if (sigFilterType) params.append('signal_type', sigFilterType);
    if (sigFilterName) params.append('signal_name', sigFilterName);
    if (sigFilterTransform) params.append('transformation_type', sigFilterTransform);
    if (sigFilterFavourite) params.append('is_favourite', sigFilterFavourite);
    if (sigFilterProduction) params.append('is_production', sigFilterProduction);

    api.get(`/api/tactical/combinations/eligible-signals?${params.toString()}`)
      .then(res => setEligibleSignals(res.data.signals || []))
      .catch(err => console.error('Error loading eligible signals:', err))
      .finally(() => setLoadingEligible(false));
  }, [sigFilterLongAsset, sigFilterShortAsset, sigFilterType, sigFilterName, sigFilterTransform, sigFilterFavourite, sigFilterProduction]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchEligible();
  }, [fetchEligible]);

  const toggleSignal = (resultId) => {
    setSelectedSignals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
        setWeights(w => { const nw = { ...w }; delete nw[resultId]; return nw; });
      } else {
        newSet.add(resultId);
        // Default to equal weight
        const count = newSet.size;
        const equalWeight = 1;
        setWeights(w => ({ ...w, [resultId]: equalWeight }));
      }
      return newSet;
    });
  };

  const handleEqualise = () => {
    const newWeights = {};
    selectedSignals.forEach(id => { newWeights[id] = 1; });
    setWeights(newWeights);
  };

  const totalWeight = Object.values(weights).reduce((sum, w) => sum + Math.abs(w || 0), 0);

  const handleRun = async () => {
    setSubmitted(true);
    if (!combinationName.trim() || selectedSignals.size < 2) {
      setError(!combinationName.trim() ? 'Enter a combination name' : 'Select at least 2 signals');
      return;
    }

    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const constituents = Array.from(selectedSignals).map(id => ({
        result_id: id,
        weight: weights[id] || 1
      }));

      const res = await api.post('/api/tactical/combination', {
        combination_name: combinationName.trim(),
        constituents,
        trade_cost: tradeCost,
        analysis_start: analysisStart,
        analysis_end: analysisEnd,
      });

      if (res.data.success) {
        setResult(res.data);
      } else {
        setError(res.data.detail || 'Combination failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  const formatPercent = (v) => v != null ? (v * 100).toFixed(2) + '%' : '-';
  const formatNum = (v) => v != null ? v.toFixed(4) : '-';

  return (
    <div className="backtest-modal-overlay">
      <div className="signal-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>New Signal Combination</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="signal-builder-body">
          {/* Name */}
          <div className="signal-builder-section">
            <h3>Combination Name</h3>
            <div className="signal-builder-field">
              <input type="text" value={combinationName} onChange={e => setCombinationName(e.target.value)}
                placeholder="e.g. Balanced Momentum Blend"
                style={submitted && !combinationName.trim() ? { borderColor: '#ffc107' } : {}} />
              {submitted && !combinationName.trim() && (
                <div style={{ color: '#ffc107', fontSize: '0.8rem', marginTop: '4px' }}>Please enter a combination name</div>
              )}
            </div>
          </div>

          {/* Signal Picker */}
          <div className="signal-builder-section">
            <h3>Select Signals {selectedSignals.size > 0 && <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#6c757d' }}>({selectedSignals.size} selected)</span>}</h3>
            {submitted && selectedSignals.size < 2 && (
              <div style={{ color: '#ffc107', fontSize: '0.8rem', marginBottom: '6px' }}>Select at least 2 signals to combine</div>
            )}
            <div className="signal-builder-field-row" style={{ marginBottom: '8px' }}>
              <div className="signal-builder-field">
                <label>Signal Name</label>
                <input type="text" placeholder="Search..." value={sigFilterName}
                  onChange={e => setSigFilterName(e.target.value)} />
              </div>
              <div className="signal-builder-field">
                <label>Signal Type</label>
                <select value={sigFilterType} onChange={e => setSigFilterType(e.target.value)}>
                  <option value="">All Types</option>
                  {(filterOptions?.signal_types || []).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="signal-builder-field">
                <label>Transformation</label>
                <select value={sigFilterTransform} onChange={e => setSigFilterTransform(e.target.value)}>
                  <option value="">All</option>
                  {(filterOptions?.transformation_types || []).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="signal-builder-field-row" style={{ marginBottom: '8px' }}>
              <div className="signal-builder-field">
                <label>Long Asset</label>
                <select value={sigFilterLongAsset} onChange={e => setSigFilterLongAsset(e.target.value)}>
                  <option value="">All</option>
                  {(filterOptions?.long_assets || []).map(a => (
                    <option key={a} value={a}>{getTickerLabel ? getTickerLabel(a) : a}</option>
                  ))}
                </select>
              </div>
              <div className="signal-builder-field">
                <label>Short Asset</label>
                <select value={sigFilterShortAsset} onChange={e => setSigFilterShortAsset(e.target.value)}>
                  <option value="">All</option>
                  {(filterOptions?.short_assets || []).map(a => (
                    <option key={a} value={a}>{getTickerLabel ? getTickerLabel(a) : a}</option>
                  ))}
                </select>
              </div>
              <div className="signal-builder-field">
                <label>Favourite</label>
                <select value={sigFilterFavourite} onChange={e => setSigFilterFavourite(e.target.value)}>
                  <option value="">All</option>
                  <option value="true">Favourites Only</option>
                  <option value="false">Non-Favourites</option>
                </select>
              </div>
              <div className="signal-builder-field">
                <label>Production</label>
                <select value={sigFilterProduction} onChange={e => setSigFilterProduction(e.target.value)}>
                  <option value="">All</option>
                  <option value="true">Production Only</option>
                  <option value="false">Non-Production</option>
                </select>
              </div>
            </div>
            {loadingEligible ? (
              <div className="backtest-loading">Loading signals...</div>
            ) : eligibleSignals.length === 0 ? (
              <div className="backtest-empty" style={{ padding: '12px' }}>No signals found. Try adjusting filters.</div>
            ) : (
              <div className="combination-signal-picker">
                <table className="backtest-table" style={{ fontSize: '0.78rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Signal Name</th>
                      <th>Type</th>
                      <th>Long Asset</th>
                      <th>Short Asset</th>
                      <th>Ann. Return</th>
                      <th>IR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleSignals.map(sig => (
                      <tr key={sig.result_id} className={selectedSignals.has(sig.result_id) ? 'backtest-selected' : ''}>
                        <td>
                          <input type="checkbox" checked={selectedSignals.has(sig.result_id)}
                            onChange={() => toggleSignal(sig.result_id)} />
                        </td>
                        <td>{sig.signal_name}</td>
                        <td>{sig.signal_type || '-'}</td>
                        <td>{getTickerLabel ? getTickerLabel(sig.long_asset) : (sig.long_asset || '-')}</td>
                        <td>{getTickerLabel ? getTickerLabel(sig.short_asset) : (sig.short_asset || '-')}</td>
                        <td>{sig.annualised_return != null ? (sig.annualised_return * 100).toFixed(2) + '%' : '-'}</td>
                        <td>{sig.information_ratio != null ? sig.information_ratio.toFixed(2) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Weights */}
          {selectedSignals.size > 0 && (
            <div className="signal-builder-section">
              <h3>
                Weights
                <button className="backtest-btn-config" style={{ marginLeft: '12px', fontSize: '0.7rem' }}
                  onClick={handleEqualise}>Equalise</button>
              </h3>
              {Array.from(selectedSignals).map(id => {
                const sig = eligibleSignals.find(s => s.result_id === id);
                const normWeight = totalWeight > 0 ? ((Math.abs(weights[id] || 0) / totalWeight) * 100).toFixed(1) + '%' : '0%';
                return (
                  <div key={id} className="combination-weight-row">
                    <span style={{ flex: 1, fontSize: '0.85rem' }}>{sig?.signal_name || id}</span>
                    <input
                      type="number"
                      className="combination-weight-input"
                      step="0.1"
                      value={weights[id] ?? 1}
                      onChange={e => setWeights(w => ({ ...w, [id]: parseFloat(e.target.value) || 0 }))}
                    />
                    <span className="combination-weight-normalised">{normWeight}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Settings */}
          {selectedSignals.size >= 2 && (
            <div className="signal-builder-section">
              <h3>Analysis Settings</h3>
              <div className="signal-builder-field-row">
                <div className="signal-builder-field">
                  <label>Start Date</label>
                  <input type="date" value={analysisStart} onChange={e => setAnalysisStart(e.target.value)} />
                </div>
                <div className="signal-builder-field">
                  <label>End Date</label>
                  <input type="date" value={analysisEnd} onChange={e => setAnalysisEnd(e.target.value)} />
                </div>
                <div className="signal-builder-field">
                  <label>Trade Cost</label>
                  <input type="number" step="0.0001" value={tradeCost} onChange={e => setTradeCost(parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>
          )}

          {/* Actions & Results */}
          <div className="signal-builder-section">
            {error && <div className="backtest-error" style={{ marginBottom: '12px' }}>{error}</div>}

            {!result ? (
              <button
                className="backtest-btn backtest-btn-primary"
                onClick={handleRun}
                disabled={running || selectedSignals.size < 2 || !combinationName.trim()}
                style={{ width: 'auto', padding: '12px 32px' }}
              >
                {running ? 'Running Combination...' : 'Run Combination'}
              </button>
            ) : (
              <div className="signal-builder-results">
                <h3>Results Preview</h3>
                <div className="signal-builder-results-grid">
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Annualised Return</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Annualised Return']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Information Ratio</span>
                    <span className="signal-builder-metric-value">{formatNum(result.results?.['Information Ratio']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">True Alpha</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['True Alpha']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Hit Rate</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Hit Rate']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Max Drawdown</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Max Drawdown']?.Current)}</span>
                  </div>
                  <div className="signal-builder-metric">
                    <span className="signal-builder-metric-label">Tracking Error</span>
                    <span className="signal-builder-metric-value">{formatPercent(result.results?.['Tracking Error']?.Current)}</span>
                  </div>
                </div>

                {result.stream_preview && result.stream_preview.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={result.stream_preview}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E6E6" />
                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => v != null ? (v * 100).toFixed(1) + '%' : '-'} labelFormatter={(l) => new Date(l).toLocaleDateString('en-AU')} />
                        <Line type="monotone" dataKey="strategy_return_cumulative" name="Strategy" stroke="#072040" dot={false} strokeWidth={2} isAnimationActive={false} />
                        <Line type="monotone" dataKey="benchmark_return_cumulative" name="Benchmark" stroke="#f0a500" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                  <button className="backtest-btn backtest-btn-primary" onClick={handleDone} style={{ width: 'auto', padding: '10px 24px' }}>
                    Done — View in Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============= Combination Config Modal =============
function CombinationConfigModal({ combinationId, onClose, getTickerLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!combinationId) return;
    setLoading(true);
    api.get(`/api/tactical/combination/${combinationId}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [combinationId]);

  if (!combinationId) return null;

  const formatPercent = (v) => v != null ? (v * 100).toFixed(2) + '%' : '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-AU') : '-';

  const constituents = data?.constituents || [];

  return (
    <div className="backtest-modal-overlay">
      <div className="backtest-modal-content" onClick={e => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>Combination: {data?.combination_name || combinationId}</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="backtest-modal-body">
          {loading && <div className="backtest-loading">Loading...</div>}
          {error && <div className="backtest-error">{error}</div>}
          {!loading && !error && data && (
            <>
              <div className="backtest-config-meta">
                <div className="backtest-config-meta-item">
                  <strong>Long Asset:</strong> {getTickerLabel ? getTickerLabel(data.long_asset) : data.long_asset}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Short Asset:</strong> {getTickerLabel ? getTickerLabel(data.short_asset) : data.short_asset}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Created:</strong> {formatDate(data.created_date)}
                </div>
                <div className="backtest-config-meta-item">
                  <strong>Frequency:</strong> {data.frequency === 'D' ? 'Daily' : 'Monthly'}
                </div>
              </div>

              <div className="backtest-config-section">
                <h4>Analysis Parameters</h4>
                <table className="backtest-params-table">
                  <tbody>
                    <tr><td className="backtest-params-label">Start Date</td><td className="backtest-params-value">{data.analysis_start}</td></tr>
                    <tr><td className="backtest-params-label">End Date</td><td className="backtest-params-value">{data.analysis_end}</td></tr>
                    <tr><td className="backtest-params-label">Trade Cost</td><td className="backtest-params-value">{data.trade_cost}</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="backtest-config-section" style={{ marginTop: '16px' }}>
                <h4>Constituent Signals</h4>
                <table className="backtest-params-table">
                  <thead>
                    <tr>
                      <th>Signal Name</th>
                      <th>Long Asset</th>
                      <th>Short Asset</th>
                      <th style={{ textAlign: 'right' }}>Raw Weight</th>
                      <th style={{ textAlign: 'right' }}>Normalised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {constituents.map((c, i) => (
                      <tr key={i}>
                        <td>{c.signal_name || c.result_id}</td>
                        <td>{getTickerLabel ? getTickerLabel(c.long_asset) : (c.long_asset || '-')}</td>
                        <td>{getTickerLabel ? getTickerLabel(c.short_asset) : (c.short_asset || '-')}</td>
                        <td style={{ textAlign: 'right' }}>{c.weight}</td>
                        <td style={{ textAlign: 'right' }}>{c.normalised_weight != null ? (c.normalised_weight * 100).toFixed(1) + '%' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============= Market Data Section =============
function MarketDataSection({ availableTickers, getTickerLabel }) {
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [tickerSearch, setTickerSearch] = useState('');
  const [chartData, setChartData] = useState({ dates: [], series: {} });
  const [loading, setLoading] = useState(false);
  const [tickerSettings, setTickerSettings] = useState({});
  const [axisConfig, setAxisConfig] = useState({
    y:  { invert: false, log: false },
    y1: { invert: false, log: false },
  });
  const [regionFilter, setRegionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [showZoom, setShowZoom] = useState(false);
  const [xStart, setXStart] = useState(0);
  const [xEnd, setXEnd] = useState(100);
  const chartRef = useRef(null);

  const metadata = availableTickers?.ticker_metadata || {};
  const dailySet = useMemo(() => new Set(availableTickers?.daily_tickers || []), [availableTickers]);
  const monthlySet = useMemo(() => new Set(availableTickers?.monthly_tickers || []), [availableTickers]);

  // All tickers across all classifications
  const allTickers = useMemo(() => {
    if (!availableTickers) return [];
    return [...new Set([...dailySet, ...monthlySet])].sort();
  }, [availableTickers, dailySet, monthlySet]);

  // Build classification tabs from metadata
  const classificationTabs = useMemo(() => {
    if (!availableTickers) return [];
    const allSet = new Set([...dailySet, ...monthlySet]);
    const groups = {};
    allSet.forEach(t => {
      const cls = metadata[t]?.asset_class || 'Unclassified';
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(t);
    });
    const sorted = Object.keys(groups).sort((a, b) => {
      if (a === 'Unclassified') return 1;
      if (b === 'Unclassified') return -1;
      return a.localeCompare(b);
    });
    return [
      { name: 'All', tickers: [...allSet] },
      ...sorted.map(name => ({ name, tickers: groups[name] })),
    ];
  }, [availableTickers, metadata, dailySet, monthlySet]);

  // Auto-select first tab
  useEffect(() => {
    if (selectedClassification === null && classificationTabs.length > 0) {
      setSelectedClassification('All');
    }
  }, [classificationTabs, selectedClassification]);

  // Get tickers for current classification tab (all frequencies)
  const tabTickers = useMemo(() => {
    if (!availableTickers || !selectedClassification) return [];
    if (selectedClassification === 'All') return allTickers;
    const tab = classificationTabs.find(t => t.name === selectedClassification);
    return tab ? tab.tickers : [];
  }, [availableTickers, selectedClassification, classificationTabs, allTickers]);

  // Unique filter options based on tickers in the current tab
  const marketFilterOptions = useMemo(() => {
    const regions = new Set(), types = new Set(), styles = new Set();
    tabTickers.forEach(t => {
      if (metadata[t]?.region) regions.add(metadata[t].region);
      if (metadata[t]?.type) types.add(metadata[t].type);
      if (metadata[t]?.style) styles.add(metadata[t].style);
    });
    return {
      regions: [...regions].sort(),
      types: [...types].sort(),
      styles: [...styles].sort(),
    };
  }, [tabTickers, metadata]);

  // Filter tickers by search (code, label, region, type, style). Search and
  // dropdown filters both scope to the selected tab; the list is sorted
  // alphabetically by display label.
  const filteredTickers = useMemo(() => {
    let list = tabTickers;
    if (regionFilter) list = list.filter(t => (metadata[t]?.region || '') === regionFilter);
    if (typeFilter) list = list.filter(t => (metadata[t]?.type || '') === typeFilter);
    if (styleFilter) list = list.filter(t => (metadata[t]?.style || '') === styleFilter);
    if (tickerSearch) {
      const q = tickerSearch.toLowerCase();
      list = list.filter(t => {
        const label = getTickerLabel(t).toLowerCase();
        return t.toLowerCase().includes(q) || label.includes(q) ||
          (metadata[t]?.region || '').toLowerCase().includes(q) ||
          (metadata[t]?.type || '').toLowerCase().includes(q) ||
          (metadata[t]?.style || '').toLowerCase().includes(q);
      });
    }
    return [...list].sort((a, b) =>
      getTickerLabel(a).localeCompare(getTickerLabel(b), undefined, { sensitivity: 'base' })
    );
  }, [tabTickers, tickerSearch, getTickerLabel, metadata, regionFilter, typeFilter, styleFilter]);

  // Determine best source for a ticker: prefer daily, fall back to monthly
  const getTickerSource = useCallback((ticker) => {
    return dailySet.has(ticker) ? 'daily' : 'monthly';
  }, [dailySet]);

  const toggleTicker = (ticker) => {
    setSelectedTickers(prev => {
      if (prev.includes(ticker)) {
        setTickerSettings(s => { const next = { ...s }; delete next[ticker]; return next; });
        return prev.filter(t => t !== ticker);
      }
      setTickerSettings(s => ({ ...s, [ticker]: { axis: 'y', transform: 'none', window: 20 } }));
      return [...prev, ticker];
    });
  };

  const updateTickerSetting = (ticker, key, value) => {
    setTickerSettings(prev => ({
      ...prev,
      [ticker]: { ...prev[ticker], [key]: value },
    }));
  };

  const applyToAll = (key, value) => {
    setTickerSettings(prev => {
      const next = { ...prev };
      selectedTickers.forEach(t => {
        if (next[t]) next[t] = { ...next[t], [key]: value };
      });
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedTickers([]);
    setTickerSettings({});
    setChartData({ dates: [], series: {} });
  };

  const handleLoadChart = async () => {
    if (selectedTickers.length === 0) return;
    setLoading(true);
    try {
      // Group tickers by source
      const bySource = {};
      selectedTickers.forEach(t => {
        const src = getTickerSource(t);
        if (!bySource[src]) bySource[src] = [];
        bySource[src].push(t);
      });

      // Fetch each source in parallel
      const fetches = Object.entries(bySource).map(([src, tickers]) =>
        api.get(`/api/tactical/market-data?source=${encodeURIComponent(src)}&tickers=${encodeURIComponent(tickers.join(','))}&max_points=0`)
          .then(res => ({ source: src, data: res.data }))
      );
      const results = await Promise.all(fetches);

      // Store each series with its own dates (for correct transforms),
      // plus a merged date union for the chart x-axis
      const dateSet = new Set();
      const perTicker = {};  // { ticker: { dates: [...], values: [...] } }
      results.forEach(({ data }) => {
        (data.dates || []).forEach(d => dateSet.add(d));
        Object.entries(data.series || {}).forEach(([ticker, values]) => {
          perTicker[ticker] = { dates: data.dates, values };
        });
      });
      const mergedDates = [...dateSet].sort();
      setChartData({ dates: mergedDates, series: perTicker });
      setXStart(0);
      setXEnd(100);
    } catch (err) {
      console.error('Error loading market data:', err);
    } finally {
      setLoading(false);
    }
  };

  const transformedSeries = useMemo(() => {
    if (!chartData.series || !Object.keys(chartData.series).length) return {};
    const mergedDates = chartData.dates;
    const result = {};
    selectedTickers.forEach(ticker => {
      const entry = chartData.series[ticker];
      if (!entry) return;
      const { dates: tickerDates, values } = entry;
      const s = tickerSettings[ticker] || { transform: 'none', window: 20 };

      // Transform on the ticker's own dense array (correct periods, fast)
      const transformed = applyTransform(values, s.transform, s.window, tickerDates);

      // Map onto the shared merged date grid for Chart.js
      if (tickerDates === mergedDates) {
        // Same date array (single source) — use directly
        result[ticker] = transformed;
      } else {
        // Build a date→value lookup and expand to merged dates
        const dateMap = {};
        tickerDates.forEach((d, i) => { dateMap[d] = transformed[i]; });
        result[ticker] = mergedDates.map(d => dateMap[d] !== undefined ? dateMap[d] : null);
      }
    });
    return result;
  }, [chartData.series, chartData.dates, selectedTickers, tickerSettings]);

  const hasChartData = chartData.dates && chartData.dates.length > 0;

  const hasRightAxis = selectedTickers.some(t => tickerSettings[t]?.axis === 'y1');

  const chartJsData = useMemo(() => {
    if (!hasChartData) return null;
    const activeTickers = selectedTickers.filter(t => transformedSeries[t]);

    // Find earliest index where any ticker has non-null data
    let startIdx = 0;
    if (activeTickers.length > 0) {
      for (let i = 0; i < chartData.dates.length; i++) {
        if (activeTickers.some(t => transformedSeries[t][i] != null)) {
          startIdx = i;
          break;
        }
      }
    }

    const trimmedDates = chartData.dates.slice(startIdx);
    // Apply x-axis zoom range
    const totalLen = trimmedDates.length;
    const zoomStart = Math.floor((xStart / 100) * totalLen);
    const zoomEnd = Math.ceil((xEnd / 100) * totalLen);
    const zoomedDates = trimmedDates.slice(zoomStart, zoomEnd);

    const datasets = activeTickers.map((ticker, i) => {
      const s = tickerSettings[ticker] || { transform: 'none', window: 20, axis: 'y' };
      const suffix = getTransformLabel(s.transform, s.window);
      return {
        label: getTickerLabel(ticker) + (suffix ? ' ' + suffix : ''),
        ticker,
        data: transformedSeries[ticker].slice(startIdx).slice(zoomStart, zoomEnd),
        borderColor: MARKET_DATA_COLORS[i % MARKET_DATA_COLORS.length],
        backgroundColor: MARKET_DATA_COLORS[i % MARKET_DATA_COLORS.length],
        yAxisID: s.axis || 'y',
        fill: false,
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0,
        spanGaps: true,
      };
    });
    return { labels: zoomedDates, datasets };
  }, [hasChartData, chartData.dates, transformedSeries, selectedTickers, getTickerLabel, tickerSettings, xStart, xEnd]);

  const chartOptions = useMemo(
    () => makeMarketDataChartOptions(axisConfig, hasRightAxis, tickerSettings),
    [axisConfig, hasRightAxis, tickerSettings]
  );

  const handleExportChart = () => {
    const chart = chartRef.current;
    if (!chart) return;
    const exportW = 1920;
    const exportH = 1080;
    const sourceH = 40;

    // Temporarily increase font sizes for export
    const opts = chart.options;
    const origLegendSize = opts.plugins.legend.labels.font.size;
    const origXSize = opts.scales.x.ticks.font.size;
    const origYSize = opts.scales.y.ticks.font.size;
    const origY1Size = opts.scales.y1?.ticks?.font?.size;
    const exportLegendSize = 17;
    const exportAxisSize = Math.round(exportLegendSize * 1.5);
    opts.plugins.legend.labels.font.size = exportLegendSize;
    opts.scales.x.ticks.font.size = exportAxisSize;
    opts.scales.y.ticks.font.size = exportAxisSize;
    if (opts.scales.y1) opts.scales.y1.ticks.font.size = exportAxisSize;

    const container = chart.canvas.parentNode;
    const origW = container.style.width;
    const origH = container.style.height;
    container.style.width = exportW + 'px';
    container.style.height = (exportH - sourceH) + 'px';
    chart.resize(exportW, exportH - sourceH);

    const canvas = document.createElement('canvas');
    canvas.width = exportW;
    canvas.height = exportH;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportW, exportH);
    ctx.drawImage(chart.canvas, 0, 0, exportW, exportH - sourceH);
    ctx.fillStyle = '#6c757d';
    ctx.font = '18px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Source: LSEG Datastream and Drummond Capital Partners', 20, exportH - 12);

    // Restore original fonts and size
    opts.plugins.legend.labels.font.size = origLegendSize;
    opts.scales.x.ticks.font.size = origXSize;
    opts.scales.y.ticks.font.size = origYSize;
    if (opts.scales.y1 && origY1Size != null) opts.scales.y1.ticks.font.size = origY1Size;
    container.style.width = origW;
    container.style.height = origH;
    chart.resize();

    const link = document.createElement('a');
    link.download = 'market-data-chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="market-data-section">
      <ManagedDisclaimer dkey="data_notice_refinitiv" as="p" className="market-data-disclaimer" />
      {/* Classification tabs */}
      <div className="market-data-classification-tabs">
        {classificationTabs.map(tab => (
          <button
            key={tab.name}
            className={`market-data-classification-tab${selectedClassification === tab.name ? ' active' : ''}`}
            onClick={() => { setSelectedClassification(tab.name); setTickerSearch(''); setRegionFilter(''); setTypeFilter(''); setStyleFilter(''); }}
          >
            {tab.name}
            <span className="market-data-tab-count">{tab.tickers.length}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <select
          className="market-data-filter-select"
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
        >
          <option value="">All Regions</option>
          {marketFilterOptions.regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select
          className="market-data-filter-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          {marketFilterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          className="market-data-filter-select"
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
        >
          <option value="">All Styles</option>
          {marketFilterOptions.styles.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="market-data-ticker-selector">
        <input
          className="market-data-ticker-search"
          type="text"
          placeholder="Search tickers in this tab..."
          value={tickerSearch}
          onChange={(e) => setTickerSearch(e.target.value)}
        />
        <div className="market-data-ticker-list">
          {filteredTickers.map(ticker => (
            <label key={ticker} className="market-data-ticker-item">
              <input
                type="checkbox"
                checked={selectedTickers.includes(ticker)}
                onChange={() => toggleTicker(ticker)}
              />
              {getTickerLabel(ticker)}
              {getTickerLabel(ticker) !== ticker && (
                <span className="market-data-ticker-code">({ticker})</span>
              )}
            </label>
          ))}
          {filteredTickers.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: '0.83rem' }}>
              No tickers found
            </div>
          )}
        </div>
      </div>

      <div className="market-data-actions">
        <button
          className="backtest-btn backtest-btn-primary"
          onClick={handleLoadChart}
          disabled={selectedTickers.length === 0 || loading}
          style={{ width: 'auto', padding: '8px 24px' }}
        >
          {loading ? 'Loading...' : 'Load Chart'}
        </button>
        {selectedTickers.length > 0 && (
          <button
            className="backtest-btn-clear"
            onClick={handleClearSelection}
            style={{ padding: '8px 16px' }}
          >
            Clear Selection
          </button>
        )}
        <span className="market-data-selected-count">
          {selectedTickers.length} ticker{selectedTickers.length !== 1 ? 's' : ''} selected
        </span>
      </div>

      {/* Ticker settings panel */}
      {selectedTickers.length > 0 && (
        <div className="market-data-ticker-settings">
          {selectedTickers.map((ticker, i) => {
            const s = tickerSettings[ticker] || { axis: 'y', transform: 'none', window: 20 };
            const tf = TRANSFORMS.find(t => t.value === s.transform);
            return (
              <div key={ticker} className="market-data-ticker-settings-row">
                <span
                  className="market-data-colour-swatch"
                  style={{ background: MARKET_DATA_COLORS[i % MARKET_DATA_COLORS.length] }}
                />
                <span className="market-data-ticker-settings-label">{getTickerLabel(ticker)}</span>
                <button
                  className={`market-data-axis-btn${s.axis === 'y' ? ' active' : ''}`}
                  onClick={() => updateTickerSetting(ticker, 'axis', 'y')}
                >L</button>
                <button
                  className={`market-data-axis-btn${s.axis === 'y1' ? ' active' : ''}`}
                  onClick={() => updateTickerSetting(ticker, 'axis', 'y1')}
                >R</button>
                <select
                  className="market-data-transform-select"
                  value={s.transform}
                  onChange={(e) => {
                    const newTf = TRANSFORMS.find(t => t.value === e.target.value);
                    updateTickerSetting(ticker, 'transform', e.target.value);
                    if (newTf?.needsWindow && !s.window) {
                      updateTickerSetting(ticker, 'window', newTf.defaultWindow);
                    }
                  }}
                >
                  {getTransformsForFreq(getTickerSource(ticker)).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                {tf?.needsWindow && (
                  <input
                    className="market-data-window-input"
                    type="number"
                    min="2"
                    value={s.window || tf.defaultWindow}
                    onChange={(e) => updateTickerSetting(ticker, 'window', parseInt(e.target.value) || 2)}
                  />
                )}
                <button
                  className="market-data-remove-btn"
                  onClick={() => toggleTicker(ticker)}
                  title="Remove"
                >×</button>
              </div>
            );
          })}
          <div className="market-data-apply-all-row">
            <span style={{ fontSize: '0.8rem', fontWeight: 500, marginRight: 8 }}>Apply to All:</span>
            <select
              className="market-data-transform-select"
              defaultValue=""
              onChange={(e) => { if (e.target.value) applyToAll('transform', e.target.value); e.target.value = ''; }}
            >
              <option value="" disabled>Transform...</option>
              {TRANSFORMS.filter(t => !t.freq).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
              className={`market-data-axis-btn`}
              onClick={() => applyToAll('axis', 'y')}
            >All L</button>
            <button
              className={`market-data-axis-btn`}
              onClick={() => applyToAll('axis', 'y1')}
            >All R</button>
          </div>
        </div>
      )}

      {hasChartData && chartJsData && (
        <div className="backtest-chart-card" style={{ width: '100%' }}>
          <h3>Market Data</h3>
          <div className="market-data-chart-toolbar">
            <div className="market-data-toolbar-group">
              <span>Left Axis:</span>
              <button
                className={`market-data-axis-btn${axisConfig.y.invert ? ' active' : ''}`}
                onClick={() => setAxisConfig(prev => ({ ...prev, y: { ...prev.y, invert: !prev.y.invert } }))}
              >Invert</button>
              <button
                className={`market-data-axis-btn${axisConfig.y.log ? ' active' : ''}`}
                onClick={() => setAxisConfig(prev => ({ ...prev, y: { ...prev.y, log: !prev.y.log } }))}
              >Log</button>
            </div>
            {hasRightAxis && (
              <div className="market-data-toolbar-group">
                <span>Right Axis:</span>
                <button
                  className={`market-data-axis-btn${axisConfig.y1.invert ? ' active' : ''}`}
                  onClick={() => setAxisConfig(prev => ({ ...prev, y1: { ...prev.y1, invert: !prev.y1.invert } }))}
                >Invert</button>
                <button
                  className={`market-data-axis-btn${axisConfig.y1.log ? ' active' : ''}`}
                  onClick={() => setAxisConfig(prev => ({ ...prev, y1: { ...prev.y1, log: !prev.y1.log } }))}
                >Log</button>
              </div>
            )}
            <div className="market-data-toolbar-group">
              <button
                className={`market-data-axis-btn${showZoom ? ' active' : ''}`}
                onClick={() => setShowZoom(prev => !prev)}
              >Zoom</button>
              {showZoom && (
                <button
                  className="market-data-axis-btn"
                  onClick={() => { setXStart(0); setXEnd(100); }}
                >Reset</button>
              )}
            </div>
            <div className="market-data-toolbar-group">
              <button
                className="market-data-axis-btn"
                onClick={handleExportChart}
                title="Export chart as PNG (PowerPoint size)"
              >Export Image</button>
            </div>
          </div>
          {showZoom && (
            <div className="market-data-zoom-panel">
              <div className="market-data-zoom-row">
                <label>X Axis</label>
                <span className="market-data-zoom-label">Start</span>
                <input type="range" min="0" max="100" value={xStart} onChange={(e) => { const v = Number(e.target.value); if (v < xEnd) setXStart(v); }} />
                <span className="market-data-zoom-label">End</span>
                <input type="range" min="0" max="100" value={xEnd} onChange={(e) => { const v = Number(e.target.value); if (v > xStart) setXEnd(v); }} />
                <span className="market-data-zoom-value">{xStart}% – {xEnd}%</span>
              </div>
            </div>
          )}
          <div style={{ height: 400 }}>
            <ChartLine ref={chartRef} data={chartJsData} options={chartOptions} />
          </div>
          <p className="market-data-chart-source">Source: LSEG Datastream and Drummond Capital Partners</p>
        </div>
      )}
    </div>
  );
}

// ============= Main App Component =============
// ============= Methodology Modal =============
function MethodologyModal({ onClose }) {
  return (
    <div className="backtest-modal-overlay" onClick={onClose}>
      <div className="backtest-modal-content" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>Calculation Methodology</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="backtest-modal-body methodology-body">
          <div className="methodology-section">
            <h3>Strategy Return</h3>
            <p><code>(long_return &times; weight) &minus; (short_return &times; weight) &minus; implementation_drag</code></p>
          </div>
          <div className="methodology-section">
            <h3>Benchmark Return</h3>
            <p>Same formula but with constant <em>average_exposure</em> instead of varying weight, and its own rebalancing drag.</p>
          </div>
          <div className="methodology-section">
            <h3>Implementation Drag (Strategy)</h3>
            <p>Cost of intentional weight changes plus drift rebalancing. Per-leg drift formula:</p>
            <p><code>|r_long &minus; r_short| / 2 &times; (1/(1+r_long) + 1/(1+r_short)) &times; weight &times; trade_cost</code></p>
          </div>
          <div className="methodology-section">
            <h3>Implementation Drag (Benchmark)</h3>
            <p>Same per-leg drift formula with constant <em>average_exposure</em> instead of varying weight.</p>
          </div>
          <div className="methodology-section">
            <h3>Annualised Return</h3>
            <p><code>cumulative^(365/N) &minus; 1</code> &nbsp;(calendar-day data, 365 days/year)</p>
          </div>
          <div className="methodology-section">
            <h3>True Alpha</h3>
            <p>Geometric cumulative active return: <code>&prod;(1 + active_return<sub>t</sub>) &minus; 1</code></p>
          </div>
          <div className="methodology-section">
            <h3>Tracking Error</h3>
            <p>Annualised standard deviation of active returns: <code>&radic;365 &times; &sigma;(active_returns)</code></p>
          </div>
          <div className="methodology-section">
            <h3>Information Ratio</h3>
            <p><code>annualised_active_return &divide; tracking_error</code></p>
          </div>
          <div className="methodology-section">
            <h3>Rolling IR</h3>
            <p>Annualised rolling active return &divide; rolling tracking error. Rolling active return is annualised: <code>(1 + rolling_cumulative)^(freq/window) &minus; 1</code></p>
          </div>
          <div className="methodology-section">
            <h3>Hit Rate</h3>
            <p>Fraction of periods with positive strategy return.</p>
          </div>
          <div className="methodology-section">
            <h3>Max Drawdown</h3>
            <p>Largest peak-to-trough decline in cumulative strategy return.</p>
          </div>
          <div className="methodology-section">
            <h3>Annualised Turnover</h3>
            <p><code>sum(|weight_changes|) &divide; number_of_years</code></p>
          </div>
          <div className="methodology-section">
            <h3>Transformations</h3>
            <ul>
              <li><strong>Logit:</strong> Standardise signal, apply logistic curve, scale to bounds</li>
              <li><strong>Logit Even:</strong> Separate standardisation for positive/negative values before logistic curve</li>
              <li><strong>Linear:</strong> Standardise signal, linearly scale to bounds</li>
              <li><strong>Percentile:</strong> Map signal to its expanding/rolling percentile rank, scale to bounds</li>
              <li><strong>Quantile:</strong> Assign to N quantile buckets, map linearly to bounds</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============= Custom Data Ticker Dropdown =============

function CustomDataTickerDropdown({ availableTickers, freq, getTickerLabel, onInsert }) {
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);

  const tickers = freq === 'D'
    ? (availableTickers?.daily_tickers || [])
    : (availableTickers?.monthly_tickers || []);
  const metadata = availableTickers?.ticker_metadata || {};

  const label = (t) => getTickerLabel ? getTickerLabel(t) : t;

  const filtered = search
    ? tickers.filter(t => {
        const q = search.toLowerCase();
        return t.toLowerCase().includes(q) ||
          label(t).toLowerCase().includes(q) ||
          (metadata[t]?.asset_class || '').toLowerCase().includes(q) ||
          (metadata[t]?.region || '').toLowerCase().includes(q) ||
          (metadata[t]?.type || '').toLowerCase().includes(q) ||
          (metadata[t]?.style || '').toLowerCase().includes(q);
      })
    : tickers;

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(t => {
      const ac = metadata[t]?.asset_class || 'Unclassified';
      if (!groups[ac]) groups[ac] = [];
      groups[ac].push(t);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unclassified') return 1;
      if (b === 'Unclassified') return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map(key => ({ name: key, tickers: groups[key] }));
  }, [filtered, metadata]);

  const totalFiltered = filtered.length;

  return (
    <div style={{ marginBottom: '8px' }}>
      <label className="signal-builder-label" style={{ marginBottom: '4px' }}>
        Insert Ticker <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#6c757d' }}>— click a ticker to insert df["TICKER"] at cursor</span>
      </label>
      <div className="signal-builder-ticker-selector">
        <div className="ticker-search-row">
          <input
            type="text"
            placeholder="Search tickers by name, label, asset class, region..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (!listOpen) setListOpen(true); }}
            onFocus={() => setListOpen(true)}
            className="signal-builder-input"
          />
          <button type="button" className="ticker-search-toggle" onClick={() => setListOpen(!listOpen)}>
            {listOpen ? '▲' : '▼'}
          </button>
        </div>

        {listOpen && (
          <div className="signal-builder-ticker-list">
            {grouped.map(group => (
              <div key={group.name}>
                <div className="ticker-group-header">{group.name}</div>
                {group.tickers.slice(0, 200).map(ticker => (
                  <div key={ticker}
                    className="signal-builder-ticker-item"
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 8px' }}
                    onClick={() => { onInsert(`df["${ticker}"]`); }}
                    title={`Insert df["${ticker}"]`}
                  >
                    <span className="ticker-item-label">{label(ticker)}</span>
                    <span className="ticker-item-code">{ticker}</span>
                  </div>
                ))}
              </div>
            ))}
            {totalFiltered > 200 && (
              <div className="signal-builder-ticker-more">...and {totalFiltered - 200} more (refine search)</div>
            )}
            {totalFiltered === 0 && <div className="signal-builder-ticker-more">No tickers found</div>}
          </div>
        )}
      </div>
    </div>
  );
}


// ============= Custom Data Section =============

function CustomDataSection({ availableTickers, getTickerLabel, onTickersChanged }) {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formFrequency, setFormFrequency] = useState('M');
  const [formExpression, setFormExpression] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Expression ref for cursor-aware insertion
  const exprRef = useRef(null);

  const loadColumns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getCustomData();
      setColumns(res.columns || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load custom data columns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadColumns(); }, [loadColumns]);

  const handleInsert = useCallback((snippet) => {
    const el = exprRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = el.value.substring(0, start) + snippet + el.value.substring(end);
    setFormExpression(newVal);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + snippet.length;
      el.focus();
    });
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormFrequency('M');
    setFormExpression('');
    setFormDescription('');
    setShowForm(false);
    setSaveError(null);
    setPreview(null);
    setPreviewError(null);
  };

  const handleEdit = (col) => {
    setEditingId(col.id);
    setFormName(col.column_name);
    setFormFrequency(col.frequency);
    setFormExpression(col.expression);
    setFormDescription(col.description || '');
    setShowForm(true);
    setSaveError(null);
    setPreview(null);
    setPreviewError(null);
  };

  const handlePreview = async () => {
    if (!formExpression.trim()) return;
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const freq = formFrequency === 'DM' ? 'M' : formFrequency;
      const res = await adminAPI.previewCustomData({ frequency: freq, expression: formExpression });
      setPreview(res);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!formExpression.trim()) { setSaveError('Expression is required'); return; }
    if (!editingId && !formName.trim()) { setSaveError('Column name is required'); return; }

    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        await adminAPI.updateCustomData(editingId, {
          expression: formExpression,
          description: formDescription || null,
          frequency: formFrequency,
        });
      } else {
        await adminAPI.createCustomData({
          column_name: formName,
          frequency: formFrequency,
          expression: formExpression,
          description: formDescription || null,
        });
      }
      resetForm();
      loadColumns();
      if (onTickersChanged) onTickersChanged();
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (col) => {
    try {
      await adminAPI.deleteCustomData(col.id);
      setDeleteConfirm(null);
      loadColumns();
      if (onTickersChanged) onTickersChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>Loading custom data columns...</div>;

  return (
    <div className="data-config-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#072040' }}>Custom Computed Columns</h3>
        {!showForm && (
          <button className="backtest-btn backtest-btn-primary" style={{ width: 'auto' }}
            onClick={() => { resetForm(); setShowForm(true); }}>
            + Add Custom Column
          </button>
        )}
      </div>

      {error && <div style={{ color: '#dc3545', marginBottom: '10px', fontSize: '0.85rem' }}>{error}</div>}

      {/* Existing columns table */}
      {columns.length > 0 && (
        <div style={{ maxHeight: '40vh', overflowY: 'auto', marginBottom: '16px' }}>
          <table className="ticker-labels-table">
            <thead>
              <tr>
                <th>Column Name</th>
                <th>Freq</th>
                <th>Expression</th>
                <th>Description</th>
                <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {columns.map(col => (
                <tr key={col.id}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{col.column_name}</td>
                  <td><span className="ticker-labels-freq">{col.frequency === 'DM' ? 'D+M' : col.frequency === 'D' ? 'Daily' : 'Monthly'}</span></td>
                  <td><code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>{col.expression}</code></td>
                  <td style={{ fontSize: '0.85rem', color: '#6c757d' }}>{col.description || '-'}</td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button className="backtest-btn-config" style={{ marginRight: '4px' }}
                      onClick={() => handleEdit(col)} title="Edit">Edit</button>
                    <button className="data-config-delete-btn"
                      onClick={() => setDeleteConfirm(col)} title="Delete">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {columns.length === 0 && !showForm && (
        <div style={{ padding: '30px', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>
          No custom columns defined yet. Click "Add Custom Column" to create one.
        </div>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <div className="signal-builder-section" style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#072040' }}>
              {editingId ? 'Edit Custom Column' : 'New Custom Column'}
            </h4>
            <button className="signal-builder-btn-remove" onClick={resetForm} style={{ width: '24px', height: '24px' }}>×</button>
          </div>

          {/* Column name + frequency */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 250px' }}>
              <label className="signal-builder-label">Column Name</label>
              <input type="text" className="signal-builder-input"
                placeholder="e.g. AU_US_10Y_Spread"
                value={formName} onChange={(e) => setFormName(e.target.value)}
                disabled={!!editingId}
                style={editingId ? { opacity: 0.6 } : {}}
              />
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <label
                className="signal-builder-label"
                title="Change the frequency on save: dropping a frequency removes the column from that Index_Returns table; adding one re-evaluates the expression against that table."
              >
                Frequency
              </label>
              <select
                className="signal-builder-input"
                value={formFrequency}
                onChange={(e) => setFormFrequency(e.target.value)}
              >
                <option value="D">Daily</option>
                <option value="M">Monthly</option>
                <option value="DM">Both (Daily + Monthly)</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '10px' }}>
            <label className="signal-builder-label">Description (optional)</label>
            <input type="text" className="signal-builder-input"
              placeholder="e.g. AU vs US 10Y bond yield spread"
              value={formDescription} onChange={(e) => setFormDescription(e.target.value)}
            />
          </div>

          {/* Expression */}
          <div style={{ marginBottom: '10px' }}>
            <label className="signal-builder-label">Expression</label>
            <textarea ref={exprRef} className="signal-builder-input"
              style={{ fontFamily: 'monospace', minHeight: '80px', resize: 'vertical' }}
              placeholder={`e.g. df["BMAU10Y(RI)"] - df["BMUS10Y(RI)"]`}
              value={formExpression} onChange={(e) => setFormExpression(e.target.value)}
              onFocus={() => {}}
            />
          </div>

          {/* Ticker dropdown — same grouped/searchable pattern as signal builder */}
          <CustomDataTickerDropdown
            availableTickers={availableTickers}
            freq={formFrequency === 'DM' ? 'M' : formFrequency}
            getTickerLabel={getTickerLabel}
            onInsert={handleInsert}
          />

          {/* Expression helpers */}
          <div style={{ marginBottom: '8px' }}>
            <label className="signal-builder-label" style={{ marginBottom: '4px' }}>Helpers</label>
            <ExpressionHelperButtons onInsert={handleInsert} />
            <NpSelectBuilder onInsert={handleInsert} />
          </div>

          {/* Preview + Save buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
            <button className="backtest-btn backtest-btn-compare" type="button"
              onClick={handlePreview} disabled={previewing || !formExpression.trim()}
              style={{ width: 'auto', padding: '6px 16px' }}>
              {previewing ? 'Previewing...' : 'Preview'}
            </button>
            <button className="backtest-btn backtest-btn-primary" type="button"
              onClick={handleSave} disabled={saving}
              style={{ width: 'auto', padding: '6px 16px' }}>
              {saving ? 'Saving...' : (editingId ? 'Update Column' : 'Save Column')}
            </button>
            <button className="backtest-btn backtest-btn-clear" type="button"
              onClick={resetForm} style={{ width: 'auto', padding: '6px 16px' }}>
              Cancel
            </button>
            {saveError && <span style={{ fontSize: '0.85rem', color: '#dc3545' }}>{saveError}</span>}
          </div>

          {/* Preview error */}
          {previewError && (
            <div style={{ color: '#dc3545', fontSize: '0.85rem', marginBottom: '8px', padding: '8px', background: '#fff5f5', borderRadius: '4px' }}>
              {previewError}
            </div>
          )}

          {/* Preview results */}
          {preview && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px' }}>
                Preview: {preview.total_rows} non-null rows
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#072040', marginBottom: '4px' }}>First 10</div>
                  <table className="ticker-labels-table" style={{ fontSize: '0.8rem' }}>
                    <thead><tr><th>Date</th><th>Value</th></tr></thead>
                    <tbody>
                      {preview.head.map((r, i) => (
                        <tr key={i}><td>{r.date}</td><td style={{ fontFamily: 'monospace' }}>{r.value != null ? r.value : 'NaN'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#072040', marginBottom: '4px' }}>Last 10</div>
                  <table className="ticker-labels-table" style={{ fontSize: '0.8rem' }}>
                    <thead><tr><th>Date</th><th>Value</th></tr></thead>
                    <tbody>
                      {preview.tail.map((r, i) => (
                        <tr key={i}><td>{r.date}</td><td style={{ fontFamily: 'monospace' }}>{r.value != null ? r.value : 'NaN'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="backtest-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="backtest-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="backtest-modal-header">
              <h3>Delete Custom Column</h3>
              <button className="backtest-modal-close" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="backtest-modal-body" style={{ padding: '20px' }}>
              <p>Are you sure you want to delete <strong>{deleteConfirm.column_name}</strong>?</p>
              <p style={{ fontSize: '0.85rem', color: '#6c757d' }}>This will remove the column from Index_Returns tables and ticker metadata.</p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                <button className="backtest-btn backtest-btn-clear" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="backtest-btn-delete" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Per-asset-class one-way transaction cost defaults (basis points). These seed the
// trade_cost of signals/composites the tactical monitor builds; an individual backtest
// config's own trade_cost overrides them. Persisted in tm_trade_costs.
function TradeCostModal({ onClose }) {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    api.get('/api/tactical/trade-costs')
      .then((r) => { setData(r.data); setVals({ ...r.data.costs }); })
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const costs = {};
      Object.entries(vals).forEach(([k, v]) => { costs[k] = v === '' ? null : Number(v); });
      const r = await api.post('/api/tactical/trade-costs', { costs });
      setVals({ ...r.data.costs }); setMsg('Saved.');
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="backtest-modal-overlay" onClick={onClose}>
      <div className="backtest-modal-content" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="backtest-modal-header">
          <h2>Transaction cost defaults</h2>
          <button className="backtest-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="backtest-modal-body">
          <p style={{ color: '#6c757d', fontSize: 13, marginTop: 0 }}>
            One-way transaction cost per asset class (basis points). These seed the trade cost of
            signals and composites the tactical monitor builds. A backtest config’s own trade cost
            overrides these. Clear a cell to revert to the built-in default.
          </p>
          {err && <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div>}
          {!data && !err && <p>Loading…</p>}
          {data && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  <th style={{ padding: '6px 4px' }}>Asset class</th>
                  <th style={{ padding: '6px 4px' }}>Cost (bps)</th>
                  <th style={{ padding: '6px 4px' }}>Default</th>
                </tr>
              </thead>
              <tbody>
                {data.regions.map((r) => (
                  <tr key={r} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 4px' }}>{r}</td>
                    <td style={{ padding: '6px 4px' }}>
                      <input type="number" step="0.5" min="0" style={{ width: 80 }} value={vals[r] ?? ''}
                             disabled={busy} onChange={(e) => setVals({ ...vals, [r]: e.target.value })} />
                    </td>
                    <td style={{ padding: '6px 4px', color: '#6c757d' }}>{data.defaults[r]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            {msg && <span style={{ color: '#6c757d' }}>{msg}</span>}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="backtest-btn backtest-btn-secondary" style={{ width: 'auto' }} onClick={onClose}>Close</button>
              <button className="backtest-btn backtest-btn-primary" style={{ width: 'auto' }} onClick={save} disabled={busy || !data}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BacktestDashboardApp() {
  const [signals, setSignals] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState({
    signal_type: '',
    signal_name: '',
    long_asset: '',
    short_asset: '',
    transformation_type: '',
    min_return: '',
    max_return: '',
    min_ir: '',
    max_ir: '',
    date_from: '',
    date_to: '',
    is_favourite: '',
    is_production: ''
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [comparedIds, setComparedIds] = useState(new Set());
  const [streamData, setStreamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [showSignalBuilder, setShowSignalBuilder] = useState(false);
  const [showBatchUpload, setShowBatchUpload] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [editConfigData, setEditConfigData] = useState(null);
  const [builderMode, setBuilderMode] = useState('new');  // 'new' | 'edit' | 'copy'
  const [builderSeq, setBuilderSeq] = useState(0);         // bump to force a fresh modal instance
  const [availableTickers, setAvailableTickers] = useState(null);
  const [tickerLabels, setTickerLabels] = useState({});

  // Tab and combination state
  const [activeTab, setActiveTab] = useState('signals');
  const [showTradeCosts, setShowTradeCosts] = useState(false);
  const [combinations, setCombinations] = useState([]);
  const [loadingCombinations, setLoadingCombinations] = useState(false);
  const [combinationSelectedIds, setCombinationSelectedIds] = useState(new Set());
  const [combinationComparedIds, setCombinationComparedIds] = useState(new Set());
  const [combinationStreamData, setCombinationStreamData] = useState([]);
  const [loadingCombinationStreams, setLoadingCombinationStreams] = useState(false);
  const [showCombinationBuilder, setShowCombinationBuilder] = useState(false);
  const [combinationModalConfig, setCombinationModalConfig] = useState(null);

  const getTickerLabel = useCallback((rawTicker) => {
    if (!rawTicker) return '-';
    return tickerLabels[rawTicker] || rawTicker;
  }, [tickerLabels]);

  // Load filter options on mount
  useEffect(() => {
    api.get('/api/tactical/filter-options')
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error('Error loading filter options:', err));
    // Load available tickers (includes labels in ticker_metadata)
    api.get('/api/tactical/available-tickers')
      .then(res => {
        setAvailableTickers(res.data);
        // Extract labels from ticker_metadata
        const meta = res.data?.ticker_metadata || {};
        const labels = {};
        Object.entries(meta).forEach(([ticker, m]) => {
          if (m.label) labels[ticker] = m.label;
        });
        setTickerLabels(labels);
      })
      .catch(err => console.error('Error loading tickers:', err));
  }, []);

  // Load signals with filters
  const loadSignals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.signal_type) params.append('signal_type', filters.signal_type);
      if (filters.signal_name) params.append('signal_name', filters.signal_name);
      if (filters.long_asset) params.append('long_asset', filters.long_asset);
      if (filters.short_asset) params.append('short_asset', filters.short_asset);
      if (filters.transformation_type) params.append('transformation_type', filters.transformation_type);
      if (filters.min_return) params.append('min_return', filters.min_return);
      if (filters.max_return) params.append('max_return', filters.max_return);
      if (filters.min_ir) params.append('min_ir', filters.min_ir);
      if (filters.max_ir) params.append('max_ir', filters.max_ir);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.is_favourite) params.append('is_favourite', filters.is_favourite);
      if (filters.is_production) params.append('is_production', filters.is_production);
      params.append('limit', '500');

      const res = await api.get(`/api/tactical/signals?${params.toString()}`);
      setSignals(res.data.results);
    } catch (err) {
      console.error('Error loading signals:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load signals on mount and when filters change
  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  // Load combinations
  const loadCombinations = useCallback(async () => {
    setLoadingCombinations(true);
    try {
      const params = new URLSearchParams();
      if (filters.signal_name) params.append('combination_name', filters.signal_name);
      if (filters.long_asset) params.append('long_asset', filters.long_asset);
      if (filters.short_asset) params.append('short_asset', filters.short_asset);
      if (filters.min_return) params.append('min_return', filters.min_return);
      if (filters.max_return) params.append('max_return', filters.max_return);
      if (filters.min_ir) params.append('min_ir', filters.min_ir);
      if (filters.max_ir) params.append('max_ir', filters.max_ir);
      if (filters.is_favourite) params.append('is_favourite', filters.is_favourite);
      if (filters.is_production) params.append('is_production', filters.is_production);

      const res = await api.get(`/api/tactical/combinations?${params.toString()}`);
      setCombinations(res.data.results || []);
    } catch (err) {
      console.error('Error loading combinations:', err);
    } finally {
      setLoadingCombinations(false);
    }
  }, [filters]);

  // Load combinations when tab is active
  useEffect(() => {
    if (activeTab === 'combinations') {
      loadCombinations();
    }
  }, [activeTab, loadCombinations]);

  // Load combination streams when compared
  useEffect(() => {
    if (combinationComparedIds.size === 0) {
      setCombinationStreamData([]);
      return;
    }
    setLoadingCombinationStreams(true);
    const ids = Array.from(combinationComparedIds).join(',');
    api.get(`/api/tactical/combinations/streams/batch?combination_ids=${ids}&max_points=500`)
      .then(res => setCombinationStreamData(res.data.streams))
      .catch(err => console.error('Error loading combination streams:', err))
      .finally(() => setLoadingCombinationStreams(false));
  }, [combinationComparedIds]);

  // Load stream data when selection changes
  // Only fetch stream data when user explicitly clicks Compare
  useEffect(() => {
    if (comparedIds.size === 0) {
      setStreamData([]);
      return;
    }

    setLoadingStreams(true);
    const ids = Array.from(comparedIds).join(',');
    api.get(`/api/tactical/signals/streams/batch?result_ids=${ids}&max_points=500`)
      .then(res => setStreamData(res.data.streams))
      .catch(err => console.error('Error loading streams:', err))
      .finally(() => setLoadingStreams(false));
  }, [comparedIds]);

  const handleCompare = () => {
    setComparedIds(new Set(selectedIds));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleToggleSignalFlag = async (resultId, flag, value) => {
    try {
      await api.post('/api/tactical/signals/toggle-flag', {
        ids: [resultId], flag, value
      });
      setSignals(prev => prev.map(s =>
        s.result_id === resultId ? { ...s, [flag]: value } : s
      ));
    } catch (err) {
      console.error('Error toggling flag:', err);
    }
  };

  const handleToggleCombinationFlag = async (combinationId, flag, value) => {
    try {
      await api.post('/api/tactical/combinations/toggle-flag', {
        ids: [combinationId], flag, value
      });
      setCombinations(prev => prev.map(c =>
        c.combination_id === combinationId ? { ...c, [flag]: value } : c
      ));
    } catch (err) {
      console.error('Error toggling flag:', err);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      signal_type: '',
      signal_name: '',
      long_asset: '',
      short_asset: '',
      transformation_type: '',
      min_return: '',
      max_return: '',
      min_ir: '',
      max_ir: '',
      date_from: '',
      date_to: '',
      is_favourite: '',
      is_production: ''
    });
  };

  const MAX_SELECTED = 20;

  const handleSelectSignal = (resultId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        if (newSet.size >= MAX_SELECTED) {
          alert(`Maximum ${MAX_SELECTED} signals can be selected at once to prevent performance issues.`);
          return prev;
        }
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === signals.length || selectedIds.size >= MAX_SELECTED) {
      setSelectedIds(new Set());
    } else {
      const limited = signals.slice(0, MAX_SELECTED).map(s => s.result_id);
      setSelectedIds(new Set(limited));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedIds.size} signal(s)? This action is permanent and cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const res = await api.post('/api/tactical/signals/delete', {
        result_ids: Array.from(selectedIds)
      });

      if (res.data.success) {
        alert(`Deleted ${res.data.deleted.results} signal(s), ${res.data.deleted.streams} stream records, and ${res.data.deleted.configs} orphaned config(s).`);
        setSelectedIds(new Set());
        setComparedIds(new Set());
        setStreamData([]);
        loadSignals();
        // Refresh filter options
        api.get('/api/tactical/filter-options')
          .then(res => setFilterOptions(res.data))
          .catch(err => console.error('Error reloading filter options:', err));
      } else {
        alert(`Error deleting signals: ${res.data.error}`);
      }
    } catch (err) {
      console.error('Error deleting signals:', err);
      alert('Failed to delete signals. Check console for details.');
    }
  };

  // Combination handlers
  const handleSelectCombination = (comboId) => {
    setCombinationSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(comboId)) newSet.delete(comboId);
      else if (newSet.size < MAX_SELECTED) newSet.add(comboId);
      return newSet;
    });
  };

  const handleSelectAllCombinations = () => {
    if (combinationSelectedIds.size === combinations.length || combinationSelectedIds.size >= MAX_SELECTED) {
      setCombinationSelectedIds(new Set());
    } else {
      setCombinationSelectedIds(new Set(combinations.slice(0, MAX_SELECTED).map(c => c.combination_id)));
    }
  };

  const handleCompareCombinations = () => {
    setCombinationComparedIds(new Set(combinationSelectedIds));
  };

  const handleDeleteSelectedCombinations = async () => {
    if (combinationSelectedIds.size === 0) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${combinationSelectedIds.size} combination(s)? This action is permanent and cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const res = await api.post('/api/tactical/combinations/delete', {
        combination_ids: Array.from(combinationSelectedIds)
      });
      if (res.data.success) {
        alert(`Deleted ${res.data.deleted.combinations} combination(s) and ${res.data.deleted.streams} stream records.`);
        setCombinationSelectedIds(new Set());
        setCombinationComparedIds(new Set());
        setCombinationStreamData([]);
        loadCombinations();
      }
    } catch (err) {
      console.error('Error deleting combinations:', err);
      alert('Failed to delete combinations.');
    }
  };

  const handleViewConfig = (configId, signalName) => {
    setModalConfig({ configId, signalName });
  };

  // Deep link from the Tactical Monitor audit modal: ?config=<id>&name=<signal>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cfg = p.get('config');
    if (cfg) { setActiveTab('signals'); setModalConfig({ configId: cfg, signalName: p.get('name') || '' }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open the signal builder in a given mode, forcing a fresh instance (via
  // builderSeq) so the modal re-initialises its internal form from initialConfig.
  const openBuilder = (mode, formData) => {
    setBuilderMode(mode);
    setEditConfigData(formData);
    setBuilderSeq(s => s + 1);
    setShowSignalBuilder(true);
  };

  // Fetch a stored config and convert it to builder-form shape. Returns null if
  // the config JSON cannot be parsed.
  const loadConfigForm = async (configId) => {
    const res = await api.get(`/api/tactical/config/${configId}`);
    const configData = res.data;
    let parsed = null;
    if (configData.config_json) {
      let jsonStr = configData.config_json;
      if (typeof jsonStr === 'string') {
        jsonStr = jsonStr.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t').replace(/\\\\/g, '\\');
      }
      try { parsed = JSON.parse(jsonStr); } catch {
        try { parsed = JSON.parse(configData.config_json); } catch { parsed = null; }
      }
    }
    return parsed ? parseConfigForForm(configData, parsed) : null;
  };

  const handleEditConfig = async (configId) => {
    try {
      const formData = await loadConfigForm(configId);
      if (formData) openBuilder('edit', formData);
      else alert('Could not parse config JSON for editing');
    } catch (err) {
      console.error('Error loading config for edit:', err);
      alert('Failed to load config for editing');
    }
  };

  const handleCopyConfig = async (configId) => {
    try {
      const formData = await loadConfigForm(configId);
      if (formData) {
        // Clear the name so the copy is saved as a NEW signal rather than
        // upserting over the original (which now keys on signal_name).
        formData.signal_name = '';
        openBuilder('copy', formData);
      } else {
        alert('Could not parse config JSON for copying');
      }
    } catch (err) {
      console.error('Error loading config for copy:', err);
      alert('Failed to load config for copying');
    }
  };

  const handleCloseModal = () => {
    setModalConfig(null);
  };

  return (
    <div className="backtest-container">
      {activeTab !== 'market_data' && activeTab !== 'data_config' && (
        <FilterSidebar
          filterOptions={filterOptions}
          filters={filters}
          onFilterChange={handleFilterChange}
          onApply={loadSignals}
          onClear={handleClearFilters}
          getTickerLabel={getTickerLabel}
        />
      )}

      <main className="backtest-main">
        <div className="backtest-header-row">
          <h1>Backtest Dashboard</h1>
          <div>
            {activeTab === 'signals' && (
              <>
                <button className="backtest-btn backtest-btn-new-signal" onClick={() => openBuilder('new', null)}>
                  + New Signal
                </button>
                <button className="backtest-btn backtest-btn-batch-upload" onClick={() => setShowBatchUpload(true)}>
                  Batch Upload
                </button>
              </>
            )}
            {activeTab === 'combinations' && (
              <button className="backtest-btn backtest-btn-new-signal" onClick={() => setShowCombinationBuilder(true)}>
                + New Combination
              </button>
            )}
            <button className="backtest-btn backtest-btn-secondary" style={{ marginLeft: 10, width: 'auto' }} onClick={() => setShowTradeCosts(true)}>
              Transaction Costs
            </button>
            <button className="backtest-btn backtest-btn-secondary" style={{ marginLeft: 10, width: 'auto' }} onClick={() => setShowMethodology(true)}>
              Methodology
            </button>
          </div>
        </div>
        {showTradeCosts && <TradeCostModal onClose={() => setShowTradeCosts(false)} />}

        <div className="backtest-tab-toggle">
          <button className={`backtest-tab ${activeTab === 'signals' ? 'active' : ''}`}
            onClick={() => setActiveTab('signals')}>Signals</button>
          <button className={`backtest-tab ${activeTab === 'combinations' ? 'active' : ''}`}
            onClick={() => setActiveTab('combinations')}>Combinations</button>
          <button className={`backtest-tab ${activeTab === 'market_data' ? 'active' : ''}`}
            onClick={() => setActiveTab('market_data')}>Market Data</button>
          <button className={`backtest-tab ${activeTab === 'data_config' ? 'active' : ''}`}
            onClick={() => setActiveTab('data_config')}>Data Configuration</button>
          <button className={`backtest-tab ${activeTab === 'custom_data' ? 'active' : ''}`}
            onClick={() => setActiveTab('custom_data')}>Custom Data</button>
        </div>

        {activeTab === 'signals' && (
          <>
            {selectedIds.size > 0 && (
              <div className="backtest-selected-count">
                <span>{selectedIds.size}/{MAX_SELECTED} signal{selectedIds.size > 1 ? 's' : ''} selected</span>
                <button className="backtest-btn backtest-btn-compare" onClick={handleCompare}>
                  Compare
                </button>
                <button className="backtest-btn backtest-btn-clear" onClick={() => { setSelectedIds(new Set()); setComparedIds(new Set()); }}>
                  Clear
                </button>
                <button className="backtest-btn-delete" onClick={handleDeleteSelected}>
                  Delete Selected
                </button>
              </div>
            )}

            <SignalsTable
              signals={signals}
              selectedIds={selectedIds}
              loading={loading}
              onSelect={handleSelectSignal}
              onSelectAll={handleSelectAll}
              onViewConfig={handleViewConfig}
              onEditConfig={handleEditConfig}
              onCopyConfig={handleCopyConfig}
              getTickerLabel={getTickerLabel}
              onToggleFlag={handleToggleSignalFlag}
            />

            {comparedIds.size > 0 && (
              <ChartsSection
                streamData={streamData}
                loading={loadingStreams}
              />
            )}
            {comparedIds.size > 0 && <ConvexitySection streamData={streamData} />}
          </>
        )}

        {activeTab === 'combinations' && (
          <>
            {combinationSelectedIds.size > 0 && (
              <div className="backtest-selected-count">
                <span>{combinationSelectedIds.size}/{MAX_SELECTED} combination{combinationSelectedIds.size > 1 ? 's' : ''} selected</span>
                <button className="backtest-btn backtest-btn-compare" onClick={handleCompareCombinations}>
                  Compare
                </button>
                <button className="backtest-btn backtest-btn-clear" onClick={() => { setCombinationSelectedIds(new Set()); setCombinationComparedIds(new Set()); }}>
                  Clear
                </button>
                <button className="backtest-btn-delete" onClick={handleDeleteSelectedCombinations}>
                  Delete Selected
                </button>
              </div>
            )}

            <CombinationsTable
              combinations={combinations}
              selectedIds={combinationSelectedIds}
              loading={loadingCombinations}
              onSelect={handleSelectCombination}
              onSelectAll={handleSelectAllCombinations}
              onViewConfig={(comboId) => setCombinationModalConfig(comboId)}
              getTickerLabel={getTickerLabel}
              onToggleFlag={handleToggleCombinationFlag}
            />

            {combinationComparedIds.size > 0 && (
              <ChartsSection
                streamData={combinationStreamData}
                loading={loadingCombinationStreams}
              />
            )}
          </>
        )}

        {activeTab === 'market_data' && (
          <MarketDataSection
            availableTickers={availableTickers}
            getTickerLabel={getTickerLabel}
          />
        )}

        {activeTab === 'data_config' && (
          <DataConfigSection
            availableTickers={availableTickers}
            tickerLabels={tickerLabels}
            onSave={(updated) => setTickerLabels(updated)}
            onMetadataSave={(updated) => setAvailableTickers(updated)}
          />
        )}

        {activeTab === 'custom_data' && (
          <CustomDataSection
            availableTickers={availableTickers}
            getTickerLabel={getTickerLabel}
            onTickersChanged={() => {
              api.get('/api/tactical/available-tickers').then(res => {
                setAvailableTickers(res.data);
                const meta = res.data?.ticker_metadata || {};
                const labels = {};
                Object.entries(meta).forEach(([ticker, m]) => {
                  if (m.label) labels[ticker] = m.label;
                });
                setTickerLabels(labels);
              }).catch(() => {});
            }}
          />
        )}
      </main>

      {modalConfig && (
        <ConfigModal
          configId={modalConfig.configId}
          signalName={modalConfig.signalName}
          onClose={handleCloseModal}
          getTickerLabel={getTickerLabel}
          onRerun={() => {
            loadSignals();
            api.get('/api/tactical/filter-options').then(res => setFilterOptions(res.data)).catch(() => {});
          }}
          onSaveAs={() => {
            loadSignals();
            api.get('/api/tactical/filter-options').then(res => setFilterOptions(res.data)).catch(() => {});
          }}
        />
      )}

      {showMethodology && (
        <MethodologyModal onClose={() => setShowMethodology(false)} />
      )}

      {showSignalBuilder && (
        <SignalBuilderModal
          key={builderSeq}
          mode={builderMode}
          onClose={() => { setShowSignalBuilder(false); setEditConfigData(null); setBuilderMode('new'); }}
          onSuccess={() => {
            loadSignals();
            api.get('/api/tactical/filter-options')
              .then(res => setFilterOptions(res.data))
              .catch(err => console.error('Error reloading filter options:', err));
          }}
          availableTickers={availableTickers}
          initialConfig={editConfigData}
          getTickerLabel={getTickerLabel}
        />
      )}

      {showBatchUpload && (
        <BatchUploadModal
          onClose={() => setShowBatchUpload(false)}
          onSuccess={() => {
            loadSignals();
            api.get('/api/tactical/filter-options')
              .then(res => setFilterOptions(res.data))
              .catch(err => console.error('Error reloading filter options:', err));
          }}
        />
      )}


      {showCombinationBuilder && (
        <CombinationBuilderModal
          onClose={() => setShowCombinationBuilder(false)}
          onSuccess={() => {
            loadCombinations();
          }}
          getTickerLabel={getTickerLabel}
          filterOptions={filterOptions}
        />
      )}

      {combinationModalConfig && (
        <CombinationConfigModal
          combinationId={combinationModalConfig}
          onClose={() => setCombinationModalConfig(null)}
          getTickerLabel={getTickerLabel}
        />
      )}

    </div>
  );
}

export default BacktestDashboardApp;
